import type { LegacyQuestion } from '../types'

export const analysisQuestions: LegacyQuestion[] = [
  {
    id: 'dgnl_data_01',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'tu-duy-logic'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu phân bổ Ngân sách Nhà nước năm 2024',
    passageText: `Biểu đồ tròn dưới đây thể hiện **Cơ cấu phân bổ Ngân sách Nhà nước** của một quốc gia trong năm 2024. Biết tổng quy mô ngân sách nhà nước năm 2024 là 2.000 **nghìn tỷ VNĐ**.

\`\`\`chart
{
  "type": "pie",
  "data": [
    {"name": "Đầu tư công", "value": 30},
    {"name": "Quốc phòng", "value": 25},
    {"name": "Giáo dục", "value": 20},
    {"name": "Y tế", "value": 15},
    {"name": "Khác", "value": 10}
  ]
}
\`\`\``,
    stem: `Số tiền quốc gia này phân bổ cho Đầu tư công trong năm 2024 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '300 nghìn tỷ VNĐ.' },
      { id: 'B', text: '500 nghìn tỷ VNĐ.' },
      { id: 'C', text: '600 nghìn tỷ VNĐ.' },
      { id: 'D', text: '800 nghìn tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `Dựa vào bảng dữ liệu, tỷ trọng ngân sách dành cho Đầu tư công là 30%.
Tổng quy mô ngân sách năm 2024 là 2.000 nghìn tỷ VNĐ.
Số tiền phân bổ cho Đầu tư công = $30\\% \\times 2000 = 600$ (nghìn tỷ VNĐ).

**Chọn C.***`,
    hint1: `Từ % tỷ trọng, muốn tìm giá trị tuyệt đối ta nhân với Tổng ngân sách.`,
    hint2: `Lấy 30% nhân với 2000 nghìn tỷ.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_02',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu phân bổ Ngân sách Nhà nước năm 2024',
    stem: `Ngân sách dành cho Quốc phòng cao hơn ngân sách dành cho Y tế bao nhiêu nghìn tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '200 nghìn tỷ VNĐ.' },
      { id: 'B', text: '300 nghìn tỷ VNĐ.' },
      { id: 'C', text: '400 nghìn tỷ VNĐ.' },
      { id: 'D', text: '500 nghìn tỷ VNĐ.' }
    ],
    answer: 'A',
    solution: `Cách 1: Tính riêng từng khoản
- Ngân sách Quốc phòng = $25\\% \\times 2000 = 500$ (nghìn tỷ).
- Ngân sách Y tế = $15\\% \\times 2000 = 300$ (nghìn tỷ).
- Chênh lệch = $500 - 300 = 200$ (nghìn tỷ).

Cách 2: Tính nhanh qua tỷ trọng
- Chênh lệch tỷ trọng = $25\\% - 15\\% = 10\\%$.
- Số tiền chênh lệch = $10\\% \\times 2000 = 200$ (nghìn tỷ).

**Chọn A.***`,
    hint1: `Có thể tính nhanh bằng cách lấy chênh lệch phần trăm giữa Quốc phòng và Y tế rồi nhân với tổng ngân sách.`,
    hint2: `Chênh lệch là 25% - 15% = 10%.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_03',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu phân bổ Ngân sách Nhà nước năm 2024',
    stem: `Số tiền chi cho Giáo dục gấp bao nhiêu lần số tiền chi cho các hạng mục Khác?`,
    choices: [
      { id: 'A', text: '1,5 lần.' },
      { id: 'B', text: '2,0 lần.' },
      { id: 'C', text: '2,5 lần.' },
      { id: 'D', text: '3,0 lần.' }
    ],
    answer: 'B',
    solution: `Vì hai khoản mục này cùng nằm trong một tổng thể (cùng nhân với Tổng ngân sách 2000), ta có thể lập tỉ số trực tiếp qua phần trăm: - Tỷ trọng Giáo dục = 20%
- Tỷ trọng Khác = 10%
- Tỉ lệ = $\\frac{20\\%}{10\\%} = 2,0$ (lần).

**Chọn B.***`,
    hint1: `Khi so sánh gấp bao nhiêu lần giữa các thành phần trong cùng 1 biểu đồ, ta chỉ cần lập tỉ số các phần trăm.`,
    hint2: `Lấy 20% chia cho 10%.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_04',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'tu-duy-logic'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu phân bổ Ngân sách Nhà nước năm 2024',
    stem: `Giả sử năm 2025, tổng ngân sách quốc gia tăng 10% so với 2024. Chính phủ quyết định số tiền chi cho Y tế trong năm 2025 cũng sẽ được tăng 20% so với số tiền của năm 2024. Hỏi tỷ trọng của ngân sách Y tế trong tổng ngân sách năm 2025 xấp xỉ bằng bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '15,00%.' },
      { id: 'B', text: '15,85%.' },
      { id: 'C', text: '16,36%.' },
      { id: 'D', text: '18,00%.' }
    ],
    answer: 'C',
    solution: `- Tổng ngân sách năm 2024 là $2000$ (nghìn tỷ).
- Tổng ngân sách năm 2025 tăng $10\\%$ $\\rightarrow$ Tổng NS 2025 = $2000 \\times 1,1 = 2200$.

- Số tiền cho Y tế năm 2024 = $15\\% \\times 2000 = 300$.
- Số tiền cho Y tế năm 2025 tăng $20\\%$ $\\rightarrow$ Y tế 2025 = $300 \\times 1,2 = 360$.

- Tỷ trọng Y tế năm 2025 = $\\frac{360}{2200} \\times 100\\% \\approx 16,36\\%$.

**Chọn C.***`,
    hint1: `Bước 1: Tính Tổng NS 2025. Bước 2: Tính Số tiền Y tế 2025. Bước 3: Lập tỉ số.`,
    hint2: `Y tế 2025 = 300 * 1.2 = 360. Tổng NS 2025 = 2000 * 1.1 = 2200.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'pie-chart', 'growth']
  },
  {
    id: 'dgnl_data_05',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu phân bổ Ngân sách Nhà nước năm 2024',
    stem: `Năm 2025, dự kiến tổng ngân sách Nhà nước sẽ tăng trưởng 15% so với năm 2024. Chính phủ đặt mục tiêu số tiền phân bổ cho Đầu tư công năm 2025 phải đạt 750 nghìn tỷ VNĐ để thực hiện các dự án trọng điểm, đồng thời số tiền cho Y tế và Giáo dục mỗi lĩnh vực tăng 10% so với số tiền phân bổ của chính lĩnh vực đó trong năm 2024. Nếu ngân sách dành cho Quốc phòng năm 2025 vẫn giữ nguyên tỷ trọng (phần trăm) trong tổng ngân sách giống như năm 2024, thì số tiền còn lại dành cho các hạng mục Khác trong năm 2025 sẽ là bao nhiêu nghìn tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '150 nghìn tỷ VNĐ.' },
      { id: 'B', text: '185 nghìn tỷ VNĐ.' },
      { id: 'C', text: '205 nghìn tỷ VNĐ.' },
      { id: 'D', text: '225 nghìn tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `- Tổng ngân sách năm 2025 = $2000 \\times 1,15 = 2300$ (nghìn tỷ).
- Số tiền Đầu tư công 2025 = $750$ (đề bài cho sẵn).
- Số tiền Y tế 2024 = $15\\% \\times 2000 = 300$ $\\rightarrow$ Y tế 2025 = $300 \\times 1,1 = 330$.
- Số tiền Giáo dục 2024 = $20\\% \\times 2000 = 400$ $\\rightarrow$ Giáo dục 2025 = $400 \\times 1,1 = 440$.
- Quốc phòng 2025 giữ nguyên **tỷ trọng** ($25\\%$) trong tổng ngân sách 2025 $\\rightarrow$ Quốc phòng 2025 = $25\\% \\times 2300 = 575$.

- Số tiền còn lại cho các hạng mục Khác năm 2025: $2300 - (750 + 330 + 440 + 575) = 2300 - 2095 = 205$ (nghìn tỷ VNĐ).

**Chọn C.***`,
    hint1: `Tính giá trị tuyệt đối của từng lĩnh vực Đầu tư công, Y tế, Giáo dục, Quốc phòng trong năm 2025 rồi lấy Tổng trừ đi.`,
    hint2: `Lưu ý Quốc phòng 2025 giữ nguyên tỷ trọng (25%) so với tổng ngân sách 2025 (2300 tỷ) chứ không phải giữ nguyên số tiền.`,
    estimatedSeconds: 90,
    tags: ['data_analysis', 'dgnl', 'pie-chart', 'optimization']
  },
  {
    id: 'dgnl_data_06',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Dân số theo độ tuổi (2020)',
    passageText: `Biểu đồ tròn dưới đây minh họa **Cơ cấu Dân số theo độ tuổi** của một quốc gia có tổng dân số là 100 **triệu người** vào năm 2020. Trong thống kê nhân khẩu học, "Tỷ lệ phụ thuộc" được tính bằng tổng số Trẻ em và Người già chia cho số Người trong độ tuổi lao động.

\`\`\`chart
{
  "type": "pie",
  "data": [
    {
      "name": "Trong độ tuổi lao động (15-64 tuổi)",
      "Tỷ trọng (%)": 65,
      "value": 65
    },
    {
      "name": "Trẻ em (0-14 tuổi)",
      "Tỷ trọng (%)": 25,
      "value": 25
    },
    {
      "name": "Người già (≥65 tuổi)",
      "Tỷ trọng (%)": 10,
      "value": 10
    }
  ],
  "options": {}
}
\`\`\``,
    stem: `Số người trong độ tuổi lao động của quốc gia này năm 2020 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '60 triệu người.' },
      { id: 'B', text: '65 triệu người.' },
      { id: 'C', text: '70 triệu người.' },
      { id: 'D', text: '75 triệu người.' }
    ],
    answer: 'B',
    solution: `Nhóm trong độ tuổi lao động chiếm 65% tổng dân số.
Tổng dân số là 100 triệu người.
Số người trong độ tuổi lao động = $65\\% \\times 100 = 65$ (triệu người).

**Chọn B.***`,
    hint1: `Lấy tỷ trọng của nhóm lao động nhân với tổng dân số.`,
    hint2: `65% của 100 triệu là bao nhiêu?`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_07',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Dân số theo độ tuổi (2020)',
    stem: `Vào năm 2020, Tỷ lệ phụ thuộc của quốc gia này xấp xỉ bằng bao nhiêu?`,
    choices: [
      { id: 'A', text: '45,5%.' },
      { id: 'B', text: '50,0%.' },
      { id: 'C', text: '53,8%.' },
      { id: 'D', text: '65,0%.' }
    ],
    answer: 'C',
    solution: `Công thức: Tỷ lệ phụ thuộc = (Số Trẻ em + Số Người già) / (Số Người trong độ tuổi lao động) * 100%.
Do tổng dân số là 100 triệu người, số lượng của mỗi nhóm bằng chính phần trăm của nhóm đó (đơn vị triệu).
- Số Trẻ em = 25
- Số Người già = 10
- Trong độ tuổi lao động = 65

Tỷ lệ phụ thuộc = $\\frac{25 + 10}{65} \\times 100\\% = \\frac{35}{65} \\times 100\\% \\approx 53,846\\%$.

**Chọn C.***`,
    hint1: `Tỷ lệ phụ thuộc = (Trẻ em + Người già) / Lao động. Thay số lượng người tương ứng vào công thức.`,
    hint2: `Tỷ lệ = (25 + 10) / 65.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'demographics']
  },
  {
    id: 'dgnl_data_08',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Dân số theo độ tuổi (2020)',
    stem: `Số trẻ em nhiều gấp bao nhiêu lần số người già?`,
    choices: [
      { id: 'A', text: '1,5 lần.' },
      { id: 'B', text: '2,0 lần.' },
      { id: 'C', text: '2,5 lần.' },
      { id: 'D', text: '3,0 lần.' }
    ],
    answer: 'C',
    solution: `Lập tỉ số giữa số trẻ em (25%) và số người già (10%): Tỉ lệ = $\\frac{25\\%}{10\\%} = 2,5$ (lần).

**Chọn C.***`,
    hint1: `Lập tỉ số trực tiếp phần trăm của 2 nhóm này.`,
    hint2: `Lấy 25 chia cho 10.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl']
  },
  {
    id: 'dgnl_data_09',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'tu-duy-logic'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Dân số theo độ tuổi (2020)',
    stem: `Mười năm sau (năm 2030), quốc gia này đối mặt với già hóa dân số. Số Người già tăng thêm 5 triệu người, số Trẻ em giảm đi 2 triệu người, trong khi số Người trong độ tuổi lao động không thay đổi so với năm 2020. Tỷ trọng của nhóm Người già trong tổng dân số lúc này xấp xỉ bao nhiêu?`,
    choices: [
      { id: 'A', text: '10,0%.' },
      { id: 'B', text: '12,5%.' },
      { id: 'C', text: '14,6%.' },
      { id: 'D', text: '15,0%.' }
    ],
    answer: 'C',
    solution: `Sự biến động dân số năm 2030 (đơn vị: triệu người): - Người già: $10 + 5 = 15$.
- Trẻ em: $25 - 2 = 23$.
- Lao động: $65$ (không đổi).
Tổng dân số mới năm 2030 = $15 + 23 + 65 = 103$ (triệu người).

Tỷ trọng Người già = $\\frac{15}{103} \\times 100\\% \\approx 14,563\\%$.

**Chọn C.***`,
    hint1: `Cần tính lại cả Tổng dân số mới của năm 2030, không được dùng tổng 100 triệu cũ.`,
    hint2: `Tổng dân số 2030 = 15 + 23 + 65 = 103 triệu.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'demographics']
  },
  {
    id: 'dgnl_data_10',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Dân số theo độ tuổi (2020)',
    stem: `Năm 2040, chính phủ đặt mục tiêu đưa "Tỷ lệ phụ thuộc" của quốc gia này về mức 50%. Dự báo đến thời điểm đó, do biến động nhân khẩu học, số lượng Trẻ em sẽ giảm đi 5 triệu người và số lượng Người già sẽ tăng thêm 10 triệu người so với năm 2020. Để đạt được Tỷ lệ phụ thuộc mục tiêu như trên, số lượng người trong độ tuổi lao động vào năm 2040 cần phải đạt tốc độ tăng trưởng là bao nhiêu phần trăm so với số lượng của nhóm này vào năm 2020?`,
    choices: [
      { id: 'A', text: '15,5%.' },
      { id: 'B', text: '23,1%.' },
      { id: 'C', text: '28,4%.' },
      { id: 'D', text: '35,0%.' }
    ],
    answer: 'B',
    solution: `Dự báo nhân khẩu học năm 2040 (đơn vị: triệu người): - Trẻ em = $25 - 5 = 20$.
- Người già = $10 + 10 = 20$.
Tổng số người phụ thuộc (Trẻ em + Người già) = $20 + 20 = 40$.

Mục tiêu Tỷ lệ phụ thuộc là 50% (0,5): Tỷ lệ phụ thuộc = $\\frac{40}{\\text{Lao động 2040}} = 0,5$
$\\Rightarrow$ Số người lao động cần có = $\\frac{40}{0,5} = 80$ (triệu người).

Số người lao động năm 2020 là 65 triệu.
Tốc độ tăng trưởng cần thiết = $\\frac{80 - 65}{65} \\times 100\\% = \\frac{15}{65} \\times 100\\% \\approx 23,076\\%$.

**Chọn B.***`,
    hint1: `Tính tổng số người phụ thuộc (trẻ em + người già) vào năm 2040, sau đó dùng công thức tỷ lệ phụ thuộc để suy ngược ra số người lao động cần thiết.`,
    hint2: `Sau khi tìm được số lao động 2040 là 80 triệu, hãy tính % tăng trưởng so với 65 triệu năm 2020.`,
    estimatedSeconds: 90,
    tags: ['data_analysis', 'dgnl', 'demographics', 'growth']
  },
  {
    id: 'dgnl_data_11',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu doanh thu theo mảng kinh doanh (2023-2024)',
    passageText: `Hai biểu đồ dưới đây thể hiện **Cơ cấu doanh thu theo mảng kinh doanh** (Sản phẩm A, Sản phẩm B, Sản phẩm C) của Công ty Z trong năm 2023 và 2024. Biết Tổng doanh thu năm 2023 là 1.000 **tỷ VNĐ** và năm 2024 là 1.500 **tỷ VNĐ**.

\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Sản phẩm A",
      "Tỷ trọng 2023 (%)": 40,
      "Tỷ trọng 2024 (%)": 30
    },
    {
      "name": "Sản phẩm B",
      "Tỷ trọng 2023 (%)": 35,
      "Tỷ trọng 2024 (%)": 50
    },
    {
      "name": "Sản phẩm C",
      "Tỷ trọng 2023 (%)": 25,
      "Tỷ trọng 2024 (%)": 20
    }
  ],
  "options": {
    "bars": [
      "Tỷ trọng 2023 (%)",
      "Tỷ trọng 2024 (%)"
    ]
  }
}
\`\`\``,
    stem: `Doanh thu từ Sản phẩm B trong năm 2023 là bao nhiêu tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '250 tỷ.' },
      { id: 'B', text: '300 tỷ.' },
      { id: 'C', text: '350 tỷ.' },
      { id: 'D', text: '400 tỷ.' }
    ],
    answer: 'C',
    solution: `Tổng doanh thu 2023 = 1.000 tỷ VNĐ.
Tỷ trọng Sản phẩm B năm 2023 = 35%.
Doanh thu Sản phẩm B (2023) = $35\\% \\times 1000 = 350$ (tỷ VNĐ).

**Chọn C.***`,
    hint1: `Lấy tỷ trọng của B năm 2023 nhân với Tổng doanh thu năm 2023.`,
    hint2: `Tỷ trọng của Sản phẩm B là 35%; lấy 35% của 1.000 tỷ VNĐ.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_12',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu doanh thu theo mảng kinh doanh (2023-2024)',
    stem: `Từ năm 2023 đến năm 2024, tỷ trọng của Sản phẩm C đã giảm bao nhiêu điểm phần trăm?`,
    choices: [
      { id: 'A', text: '3 điểm phần trăm.' },
      { id: 'B', text: '5 điểm phần trăm.' },
      { id: 'C', text: '10 điểm phần trăm.' },
      { id: 'D', text: '20 điểm phần trăm.' }
    ],
    answer: 'B',
    solution: `Sự chênh lệch giữa hai tỷ lệ phần trăm được đo lường bằng "điểm phần trăm".
- Tỷ trọng C (2023) = 25%.
- Tỷ trọng C (2024) = 20%.
Giảm: $25\\% - 20\\% = 5$ (điểm phần trăm).

**Chọn B.***`,
    hint1: `Chỉ cần lấy hiệu số giữa hai tỷ trọng (%) của Sản phẩm C.`,
    hint2: `Lấy 25% trừ 20%; đơn vị của kết quả là điểm phần trăm.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'percentage-point']
  },
  {
    id: 'dgnl_data_13',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu doanh thu theo mảng kinh doanh (2023-2024)',
    stem: `Đánh giá sự thay đổi về doanh thu tuyệt đối của Sản phẩm A năm 2024 so với năm 2023, nhận định nào sau đây là đúng?`,
    choices: [
      { id: 'A', text: 'Doanh thu giảm 10%.' },
      { id: 'B', text: 'Doanh thu giảm 50 tỷ.' },
      { id: 'C', text: 'Doanh thu không thay đổi.' },
      { id: 'D', text: 'Doanh thu tăng 50 tỷ.' }
    ],
    answer: 'D',
    solution: `Tính giá trị tuyệt đối doanh thu của A qua các năm: - Doanh thu A (2023) = $40\\% \\times 1000 = 400$ (tỷ).
- Doanh thu A (2024) = $30\\% \\times 1500 = 450$ (tỷ).
So sánh: $450 - 400 = 50$ (tỷ).
Vậy doanh thu Sản phẩm A đã tăng thêm 50 tỷ (mặc dù tỷ trọng phần trăm giảm từ 40% xuống 30%).

**Lưu ý: ** Không được nhầm lẫn giữa giảm tỷ trọng và giảm doanh thu tuyệt đối.

**Chọn D.***`,
    hint1: `Phải tính riêng Doanh thu A của năm 2023 và 2024 ra tiền mặt rồi mới so sánh. Đừng nhìn phần trăm giảm mà kết luận giảm.`,
    hint2: `A 2023 = 400 tỷ. A 2024 = 450 tỷ.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_14',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu doanh thu theo mảng kinh doanh (2023-2024)',
    stem: `Tốc độ tăng trưởng doanh thu của Sản phẩm B trong năm 2024 so với năm 2023 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '50%.' },
      { id: 'B', text: '85,7%.' },
      { id: 'C', text: '100,5%.' },
      { id: 'D', text: '114,3%.' }
    ],
    answer: 'D',
    solution: `Tính doanh thu tuyệt đối của B: - Doanh thu B (2023) = $35\\% \\times 1000 = 350$ (tỷ).
- Doanh thu B (2024) = $50\\% \\times 1500 = 750$ (tỷ).

Tốc độ tăng trưởng = $\\frac{750 - 350}{350} \\times 100\\% = \\frac{400}{350} \\times 100\\% \\approx 114,285\\%$.

**Chọn D.***`,
    hint1: `Tính doanh thu 2023 và 2024 của Sản phẩm B. Sau đó áp dụng công thức Tăng trưởng = (Sau - Trước)/Trước.`,
    hint2: `(750 - 350) / 350.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_15',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu doanh thu theo mảng kinh doanh (2023-2024)',
    stem: `Dự kiến năm 2025, Tổng doanh thu của Công ty Z sẽ tăng 20% so với năm 2024. Giám đốc đặt mục tiêu doanh thu từ Sản phẩm C trong năm 2025 sẽ đạt mức 450 tỷ VNĐ. Hỏi để đạt được mục tiêu này, tỷ trọng của Sản phẩm C trong năm 2025 phải đạt bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '20%.' },
      { id: 'B', text: '25%.' },
      { id: 'C', text: '30%.' },
      { id: 'D', text: '35%.' }
    ],
    answer: 'B',
    solution: `Tổng doanh thu 2024 = 1.500 tỷ.
Dự kiến Tổng doanh thu 2025 (tăng 20%) = $1.500 \\times 1,2 = 1.800$ (tỷ).
Mục tiêu doanh thu C năm 2025 = 450 tỷ.
Tỷ trọng mục tiêu của C = $\\frac{450}{1.800} \\times 100\\% = 25\\%$.

**Chọn B.***`,
    hint1: `Tính tổng doanh thu mới của năm 2025, sau đó tính tỷ trọng của 450 tỷ trong tổng mới.`,
    hint2: `Tổng mới = 1500 * 1,2 = 1800.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'optimization']
  },
  {
    id: 'dgnl_data_16',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu doanh thu theo mảng kinh doanh (2023-2024)',
    stem: `Năm 2025, Công ty Z quyết định ra mắt thêm dòng Sản phẩm D mới. Dự kiến Tổng doanh thu năm 2025 của toàn công ty sẽ tăng 20% so với năm 2024. Biết rằng trong năm 2025, doanh thu của Sản phẩm A và Sản phẩm B đều giữ nguyên mức doanh thu như năm 2024, trong khi doanh thu của Sản phẩm C giảm 20% so với năm 2024 do sự cạnh tranh từ các đối thủ mới. Hỏi Sản phẩm D sẽ chiếm tỷ trọng bao nhiêu phần trăm trong cơ cấu doanh thu của Công ty Z vào năm 2025?`,
    choices: [
      { id: 'A', text: '18,5%.' },
      { id: 'B', text: '20,0%.' },
      { id: 'C', text: '22,2%.' },
      { id: 'D', text: '25,0%.' }
    ],
    answer: 'B',
    solution: `Tổng doanh thu 2025 (tăng 20% so với 1500) = $1500 \\times 1,2 = 1800$ (tỷ).

Phân tích doanh thu từng sản phẩm cũ trong năm 2025: - Sản phẩm A (2025) = A (2024) = $30\\% \\times 1500 = 450$ (tỷ).
- Sản phẩm B (2025) = B (2024) = $50\\% \\times 1500 = 750$ (tỷ).
- Sản phẩm C (2024) = $20\\% \\times 1500 = 300$ (tỷ).
Vì C giảm 20% doanh thu $\\rightarrow$ Sản phẩm C (2025) = $300 \\times (1 - 0,2) = 240$ (tỷ).

Tổng doanh thu của A, B, C năm 2025 = $450 + 750 + 240 = 1440$ (tỷ).
Suy ra doanh thu của Sản phẩm D = $1800 - 1440 = 360$ (tỷ).

Tỷ trọng của Sản phẩm D = $\\frac{360}{1800} \\times 100\\% = 20,0\\%$.

*(Lưu ý: Nếu trong tài liệu gốc đáp án B ghi là 20,8% thì có thể là do lỗi đánh máy/nhận dạng nhầm số 0 thành số 8, đáp án chính xác về mặt toán học là 20,0%)*.

**Chọn B.***`,
    hint1: `Tính doanh thu bằng tiền mặt của từng sản phẩm A, B, C vào năm 2025, rồi lấy Tổng doanh thu (1800 tỷ) trừ đi.`,
    hint2: `A=450, B=750, C=240. D = 1800 - (450+750+240) = 360.`,
    estimatedSeconds: 90,
    tags: ['data_analysis', 'dgnl', 'optimization']
  }
,
  {
    id: 'dgnl_data_17',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Doanh thu và Lợi nhuận của Tập đoàn XYZ (2023)',
    passageText: `Hai biểu đồ tròn dưới đây thể hiện **Cơ cấu Doanh thu** và **Cơ cấu Lợi nhuận** của Tập đoàn đa ngành XYZ trong năm 2023. Được biết, trong năm 2023, Tổng doanh thu của tập đoàn đạt 50.000 **tỷ VNĐ** và Tổng lợi nhuận đạt 5.000 **tỷ VNĐ**.

\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Bất động sản",
      "Cơ cấu Doanh thu (%)": 40,
      "Cơ cấu Lợi nhuận (%)": 60
    },
    {
      "name": "Bán lẻ",
      "Cơ cấu Doanh thu (%)": 30,
      "Cơ cấu Lợi nhuận (%)": 10
    },
    {
      "name": "Sản xuất",
      "Cơ cấu Doanh thu (%)": 20,
      "Cơ cấu Lợi nhuận (%)": 25
    },
    {
      "name": "Khác",
      "Cơ cấu Doanh thu (%)": 10,
      "Cơ cấu Lợi nhuận (%)": 5
    }
  ],
  "options": {
    "bars": [
      "Cơ cấu Doanh thu (%)",
      "Cơ cấu Lợi nhuận (%)"
    ]
  }
}
\`\`\``,
    stem: `Lợi nhuận từ mảng Sản xuất của Tập đoàn XYZ trong năm 2023 là bao nhiêu tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '1.000.' },
      { id: 'B', text: '1.250.' },
      { id: 'C', text: '1.500.' },
      { id: 'D', text: '2.000.' }
    ],
    answer: 'B',
    solution: `Tổng lợi nhuận của tập đoàn năm 2023 là 5.000 tỷ VNĐ.
Tỷ trọng lợi nhuận mảng Sản xuất là 25%.
Lợi nhuận mảng Sản xuất = $25\% \times 5000 = 1.250$ (tỷ VNĐ).

**Chọn B.***`,
    hint1: `Sử dụng Tổng lợi nhuận nhân với phần trăm Cơ cấu Lợi nhuận của mảng Sản xuất.`,
    hint2: `Lấy 25% nhân với 5000.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_18',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Doanh thu và Lợi nhuận của Tập đoàn XYZ (2023)',
    stem: `Mảng kinh doanh nào của Tập đoàn XYZ có Biên lợi nhuận (tỷ lệ Lợi nhuận / Doanh thu) thấp nhất?`,
    choices: [
      { id: 'A', text: 'Bất động sản.' },
      { id: 'B', text: 'Bán lẻ.' },
      { id: 'C', text: 'Sản xuất.' },
      { id: 'D', text: 'Khác.' }
    ],
    answer: 'B',
    solution: `Biên lợi nhuận = Lợi nhuận / Doanh thu.
- **Bất động sản**: Lợi nhuận = $60\% \times 5000 = 3000$. Doanh thu = $40\% \times 50000 = 20000$. Biên lợi nhuận = $\frac{3000}{20000} = 15\%$.
- **Bán lẻ**: Lợi nhuận = $10\% \times 5000 = 500$. Doanh thu = $30\% \times 50000 = 15000$. Biên lợi nhuận = $\frac{500}{15000} \approx 3,33\%$.
- **Sản xuất**: Lợi nhuận = $25\% \times 5000 = 1250$. Doanh thu = $20\% \times 50000 = 10000$. Biên lợi nhuận = $\frac{1250}{10000} = 12,5\%$.
- **Khác**: Lợi nhuận = $5\% \times 5000 = 250$. Doanh thu = $10\% \times 50000 = 5000$. Biên lợi nhuận = $\frac{250}{5000} = 5\%$.

Vậy mảng **Bán lẻ** có biên lợi nhuận thấp nhất ($3,33\%$).

**Chọn B.***`,
    hint1: `Tính Doanh thu tuyệt đối và Lợi nhuận tuyệt đối của từng mảng rồi lập tỉ số.`,
    hint2: `Lưu ý lấy đúng Tổng lợi nhuận là 5000 và Tổng doanh thu là 50.000.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'profit-margin']
  },
  {
    id: 'dgnl_data_19',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Doanh thu và Lợi nhuận của Tập đoàn XYZ (2023)',
    stem: `Nếu trong năm 2024, doanh thu mảng Bán lẻ tăng 20%, trong khi doanh thu các mảng khác không đổi, thì tỷ trọng doanh thu mảng Bán lẻ trong tổng doanh thu mới của tập đoàn sẽ xấp xỉ bao nhiêu?`,
    choices: [
      { id: 'A', text: '32,0%.' },
      { id: 'B', text: '36,0%.' },
      { id: 'C', text: '34,0%.' },
      { id: 'D', text: '35,0%.' }
    ],
    answer: 'C',
    solution: `Doanh thu mảng Bán lẻ năm 2023 = $30\% \times 50.000 = 15.000$ (tỷ).
Năm 2024, doanh thu Bán lẻ tăng $20\%$ $\rightarrow$ Doanh thu Bán lẻ mới = $15.000 \times 1,2 = 18.000$ (tỷ).
Doanh thu các mảng khác không đổi, nên Tổng doanh thu mới = $50.000 - 15.000 + 18.000 = 53.000$ (tỷ).
Tỷ trọng doanh thu mảng Bán lẻ mới = $\frac{18.000}{53.000} \times 100\% \approx 33,96\% \approx 34,0\%$.

**Chọn C.***`,
    hint1: `Khi mảng Bán lẻ tăng thì Tổng doanh thu của toàn tập đoàn cũng tăng theo. Hãy tính lại cả hai giá trị này.`,
    hint2: `Doanh thu Bán lẻ tăng 3000 tỷ nên Tổng doanh thu cũng tăng thêm 3000 tỷ thành 53.000 tỷ.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_20',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Doanh thu và Lợi nhuận của Tập đoàn XYZ (2023)',
    stem: `Giả sử biên lợi nhuận của từng mảng kinh doanh là không thay đổi so với năm 2023. Để tổng lợi nhuận của toàn tập đoàn đạt 6.000 tỷ VNĐ chỉ nhờ vào việc tăng trưởng doanh thu của mảng Sản xuất (các mảng khác giữ nguyên), thì doanh thu mảng Sản xuất phải đạt bao nhiêu tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '12.000.' },
      { id: 'B', text: '15.000.' },
      { id: 'C', text: '18.000.' },
      { id: 'D', text: '20.000.' }
    ],
    answer: 'C',
    solution: `Tổng lợi nhuận năm 2023 là 5.000 tỷ. Để đạt 6.000 tỷ, lợi nhuận cần tăng thêm: $6.000 - 5.000 = 1.000$ (tỷ).
Vì lợi nhuận tăng thêm này hoàn toàn đến từ mảng Sản xuất, nên lợi nhuận mảng Sản xuất cần tăng thêm 1.000 tỷ.

Theo câu trước, Biên lợi nhuận của mảng Sản xuất = $12,5\%$.
Doanh thu mảng Sản xuất cần tăng thêm = $\frac{\text{Lợi nhuận tăng thêm}}{\text{Biên lợi nhuận}} = \frac{1.000}{12,5\%} = 8.000$ (tỷ).

Doanh thu mảng Sản xuất năm 2023 = $20\% \times 50.000 = 10.000$ (tỷ).
Doanh thu mảng Sản xuất cần đạt được = $10.000 + 8.000 = 18.000$ (tỷ VNĐ).

**Chọn C.***`,
    hint1: `Tính biên lợi nhuận mảng Sản xuất (12.5%). Lợi nhuận cần tăng thêm 1000 tỷ. Suy ra doanh thu cần tăng bao nhiêu?`,
    hint2: `Doanh thu cần tăng = 1000 / 0.125 = 8000 tỷ. Cộng với doanh thu cũ là ra đáp án.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'optimization']
  },
  {
    id: 'dgnl_data_21',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu Doanh thu và Lợi nhuận của Tập đoàn XYZ (2023)',
    stem: `Trong năm 2024, Ban lãnh đạo Tập đoàn XYZ quyết định tái cấu trúc hoạt động kinh doanh. Dự kiến Tổng doanh thu năm 2024 của tập đoàn không đổi so với năm 2023, nhưng doanh thu của mảng Bất động sản sẽ sụt giảm 20% so với năm 2023 do biến động thị trường. Tập đoàn đặt mục tiêu dùng toàn bộ phần doanh thu sụt giảm từ Bất động sản này để chuyển sang gia tăng doanh thu cho mảng Khác. Nếu nhờ các biện pháp tối ưu chi phí, Biên lợi nhuận (Tỷ lệ Lợi nhuận/Doanh thu) của mảng Khác trong năm 2024 được cải thiện mạnh mẽ lên mức 25%, thì lợi nhuận thu được từ mảng Khác trong năm 2024 sẽ cao gấp bao nhiêu lần so với chính mảng này trong năm 2023?`,
    choices: [
      { id: 'A', text: '5 lần.' },
      { id: 'B', text: '7 lần.' },
      { id: 'C', text: '9 lần.' },
      { id: 'D', text: '11 lần.' }
    ],
    answer: 'C',
    solution: `- Doanh thu Bất động sản 2023 = $40\% \times 50.000 = 20.000$ (tỷ).
- Doanh thu Bất động sản sụt giảm $20\%$ = $20.000 \times 20\% = 4.000$ (tỷ).
- Toàn bộ $4.000$ tỷ này được chuyển sang doanh thu mảng Khác.
- Doanh thu mảng Khác 2023 = $10\% \times 50.000 = 5.000$ (tỷ).
$\rightarrow$ Doanh thu mảng Khác 2024 = $5.000 + 4.000 = 9.000$ (tỷ).

- Lợi nhuận mảng Khác 2024 (với biên lợi nhuận mới $25\%$) = $9.000 \times 25\% = 2.250$ (tỷ).
- Lợi nhuận mảng Khác 2023 = $5\% \times 5.000 = 250$ (tỷ).
- Lợi nhuận mảng Khác năm 2024 gấp Lợi nhuận mảng Khác năm 2023 số lần là: $\frac{2.250}{250} = 9$ (lần).

**Chọn C.***`,
    hint1: `Tìm phần doanh thu bị giảm của BĐS bằng bao nhiêu tiền mặt (20% của 20000 tỷ). Cộng phần đó vào mảng Khác.`,
    hint2: `Tính lợi nhuận mới của mảng Khác bằng Doanh thu mới * 25%. Rồi chia cho lợi nhuận cũ (5% của 5000).`,
    estimatedSeconds: 90,
    tags: ['data_analysis', 'dgnl', 'optimization']
  }
,
  {
    id: 'dgnl_data_22',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Doanh thu và Lợi nhuận của Công ty X (2020-2023)',
    passageText: `Biểu đồ cột dưới đây thể hiện Doanh thu và Lợi nhuận (đơn vị: Tỷ VNĐ) của Công ty X qua 4 năm (2020 - 2023).
- **2020**: Doanh thu 1200, Lợi nhuận 300
- **2021**: Doanh thu 1100, Lợi nhuận 220
- **2022**: Doanh thu 1450, Lợi nhuận 435
- **2023**: Doanh thu 1600, Lợi nhuận 560`,
    stem: `Năm 2022, doanh thu của công ty đã tăng trưởng bao nhiêu phần trăm so với năm 2021?`,
    choices: [
      { id: 'A', text: '24,14%.' },
      { id: 'B', text: '31,82%.' },
      { id: 'C', text: '35,50%.' },
      { id: 'D', text: '18,23%.' }
    ],
    answer: 'B',
    solution: `Doanh thu năm 2022 là 1450 tỷ VNĐ.
Doanh thu năm 2021 là 1100 tỷ VNĐ.
Tốc độ tăng trưởng = $\frac{1450 - 1100}{1100} \times 100\% = \frac{350}{1100} \times 100\% \approx 31,82\%$.

**Chọn B.***`,
    hint1: `Công thức tính tốc độ tăng trưởng: (Năm sau - Năm trước) / Năm trước * 100%.`,
    hint2: `Lấy (1450 - 1100) / 1100.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'bar-chart', 'growth']
  },
  {
    id: 'dgnl_data_23',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Doanh thu và Lợi nhuận của Công ty X (2020-2023)',
    stem: `Biên lợi nhuận của công ty trong năm 2023 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '25%.' },
      { id: 'B', text: '30%.' },
      { id: 'C', text: '35%.' },
      { id: 'D', text: '40%.' }
    ],
    answer: 'C',
    solution: `Năm 2023: Lợi nhuận = 560 tỷ VNĐ.
Doanh thu = 1600 tỷ VNĐ.
Biên lợi nhuận = $\frac{\text{Lợi nhuận}}{\text{Doanh thu}} \times 100\% = \frac{560}{1600} \times 100\% = 35\%$.

**Chọn C.***`,
    hint1: `Biên lợi nhuận = Lợi nhuận / Doanh thu * 100%.`,
    hint2: `Lấy 560 chia cho 1600.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'profit-margin']
  },
  {
    id: 'dgnl_data_24',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Doanh thu và Lợi nhuận của Công ty X (2020-2023)',
    stem: `Trung bình mỗi năm trong giai đoạn 2020 - 2023, công ty đạt được bao nhiêu lợi nhuận?`,
    choices: [
      { id: 'A', text: '356,5 tỷ VNĐ.' },
      { id: 'B', text: '382,2 tỷ VNĐ.' },
      { id: 'C', text: '378,75 tỷ VNĐ.' },
      { id: 'D', text: '415,5 tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `Lợi nhuận các năm: 2020 = 300
2021 = 220
2022 = 435
2023 = 560
Tổng lợi nhuận = $300 + 220 + 435 + 560 = 1515$ (tỷ VNĐ).
Lợi nhuận trung bình = $\frac{1515}{4} = 378,75$ (tỷ VNĐ).

**Chọn C.***`,
    hint1: `Tính tổng lợi nhuận của cả 4 năm rồi chia cho 4.`,
    hint2: `(300 + 220 + 435 + 560) / 4.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'average']
  },
  {
    id: 'dgnl_data_25',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Doanh thu và Lợi nhuận của Công ty X (2020-2023)',
    stem: `Giả sử trong năm 2024, công ty X đặt mục tiêu Doanh thu tăng trưởng 15% so với năm 2023, đồng thời nỗ lực tiết giảm Chi phí (Biết: Doanh thu = Lợi nhuận + Chi phí) sao cho Chi phí của năm 2024 chỉ bằng 90% Chi phí của năm 2023. Nếu đạt được mục tiêu này, Lợi nhuận năm 2024 của công ty X sẽ là bao nhiêu Tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '854 tỷ VNĐ.' },
      { id: 'B', text: '884 tỷ VNĐ.' },
      { id: 'C', text: '904 tỷ VNĐ.' },
      { id: 'D', text: '924 tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `- Năm 2023: Doanh thu = 1600; Lợi nhuận = 560 $\rightarrow$ Chi phí 2023 = $1600 - 560 = 1040$ (tỷ).
- Năm 2024: + Doanh thu 2024 = $1600 \times (1 + 15\%) = 1840$ (tỷ).
  + Chi phí 2024 = $1040 \times 90\% = 936$ (tỷ).
  + Lợi nhuận 2024 = Doanh thu - Chi phí = $1840 - 936 = 904$ (tỷ).

**Chọn C.***`,
    hint1: `Từ số liệu 2023, tính Chi phí năm 2023. Sau đó tính Doanh thu 2024 và Chi phí 2024 theo các tỷ lệ mục tiêu.`,
    hint2: `Lợi nhuận 2024 = Doanh thu 2024 - Chi phí 2024.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'optimization']
  },
  {
    id: 'dgnl_data_26',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Doanh thu và Lợi nhuận của Công ty X (2020-2023)',
    stem: `Công ty X lên kế hoạch cho giai đoạn 5 năm tiếp theo (2024 - 2028) với mục tiêu Tổng lợi nhuận tích lũy trong toàn bộ 5 năm phải đạt 4.500 tỷ VNĐ. Biết rằng, trong mảng kinh doanh truyền thống hiện tại, lợi nhuận mỗi năm (từ 2024 đến 2028) được duy trì ổn định bằng với mức lợi nhuận của năm 2023. Đối với dòng sản phẩm mới, Biên lợi nhuận (Tỷ lệ Lợi nhuận/Doanh thu) dự kiến đạt 25%. Hỏi mảng sản phẩm mới phải đem về bình quân mỗi năm bao nhiêu Tỷ VNĐ Doanh thu để giúp công ty hoàn thành mục tiêu Lợi nhuận tích lũy nói trên?`,
    choices: [
      { id: 'A', text: '1.050 tỷ VNĐ.' },
      { id: 'B', text: '1.240 tỷ VNĐ.' },
      { id: 'C', text: '1.360 tỷ VNĐ.' },
      { id: 'D', text: '1.420 tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `- Lợi nhuận năm 2023 là 560 tỷ.
- Lợi nhuận mảng truyền thống trong 5 năm (2024-2028) = $560 \times 5 = 2.800$ (tỷ).
- Mục tiêu tổng lợi nhuận 5 năm là 4.500 tỷ $\rightarrow$ Lợi nhuận mảng mới cần đạt trong 5 năm = $4.500 - 2.800 = 1.700$ (tỷ).
- Lợi nhuận bình quân mỗi năm của mảng mới = $1.700 / 5 = 340$ (tỷ/năm).
- Biên lợi nhuận mảng mới = $25\%$ $\rightarrow$ Doanh thu bình quân mỗi năm của mảng mới = $340 / 25\% = 1.360$ (tỷ/năm).

**Chọn C.***`,
    hint1: `Tính phần lợi nhuận còn thiếu so với mục tiêu 4500 tỷ sau khi đã trừ đi phần lợi nhuận ổn định từ mảng truyền thống (5 năm).`,
    hint2: `Lợi nhuận thiếu = 1700 tỷ (cho 5 năm) $\rightarrow$ 340 tỷ/năm. Từ Biên lợi nhuận suy ra Doanh thu = Lợi nhuận / 25%.`,
    estimatedSeconds: 75,
    tags: ['data_analysis', 'dgnl', 'optimization', 'planning']
  },
  {
    id: 'dgnl_data_27',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Biến động Lạm phát và Lãi suất tiết kiệm (2019 - 2023)',
    passageText: `Biểu đồ đường dưới đây biểu diễn sự biến động của **Tỷ lệ Lạm phát (%)** và **Lãi suất tiết kiệm trung bình (%)** của một quốc gia trong giai đoạn 2019 - 2023. Biết Lãi suất thực = Lãi suất tiết kiệm - Tỷ lệ Lạm phát.
- **2019**: Lạm phát = 2,5%; Lãi suất tiết kiệm = 6,0%
- **2020**: Lạm phát = 3,2%; Lãi suất tiết kiệm = 5,5%
- **2021**: Lạm phát = 1,8%; Lãi suất tiết kiệm = 4,0%
- **2022**: Lạm phát = 4,5%; Lãi suất tiết kiệm = 7,5%
- **2023**: Lạm phát = 3,0%; Lãi suất tiết kiệm = 5,0%`,
    stem: `Năm nào Lãi suất tiết kiệm đạt mức cao nhất trong giai đoạn khảo sát?`,
    choices: [
      { id: 'A', text: '2019.' },
      { id: 'B', text: '2020.' },
      { id: 'C', text: '2022.' },
      { id: 'D', text: '2023.' }
    ],
    answer: 'C',
    solution: `Quan sát số liệu hoặc đồ thị đường "Lãi suất tiết kiệm" (đường màu xanh), đỉnh cao nhất rơi vào năm 2022 với mức 7,5%.

**Chọn C.***`,
    hint1: `Tìm điểm cao nhất trên đường Lãi suất tiết kiệm.`,
    hint2: `Đó là năm 2022 với 7.5%.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'line-chart']
  },
  {
    id: 'dgnl_data_28',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Biến động Lạm phát và Lãi suất tiết kiệm (2019 - 2023)',
    stem: `Lãi suất thực của quốc gia này trong năm 2020 là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '2,0%.' },
      { id: 'B', text: '2,3%.' },
      { id: 'C', text: '2,5%.' },
      { id: 'D', text: '3,0%.' }
    ],
    answer: 'B',
    solution: `Năm 2020: Lãi suất tiết kiệm = $5,5\%$
Tỷ lệ Lạm phát = $3,2\%$
Lãi suất thực = Lãi suất tiết kiệm - Tỷ lệ Lạm phát = $5,5\% - 3,2\% = 2,3\%$.

**Chọn B.***`,
    hint1: `Sử dụng công thức đã cho: Lãi suất thực = Lãi suất tiết kiệm - Tỷ lệ Lạm phát.`,
    hint2: `Lấy 5.5 - 3.2.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'formula']
  },
  {
    id: 'dgnl_data_29',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Biến động Lạm phát và Lãi suất tiết kiệm (2019 - 2023)',
    stem: `Tỷ lệ lạm phát trung bình của quốc gia này trong cả giai đoạn 2019 - 2023 là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '3,0%.' },
      { id: 'B', text: '3,2%.' },
      { id: 'C', text: '3,5%.' },
      { id: 'D', text: '4,0%.' }
    ],
    answer: 'A',
    solution: `Tỷ lệ lạm phát các năm: 2019: 2,5%
2020: 3,2%
2021: 1,8%
2022: 4,5%
2023: 3,0%
Trung bình = $\frac{2,5 + 3,2 + 1,8 + 4,5 + 3,0}{5} = \frac{15}{5} = 3,0\%$.

**Chọn A.***`,
    hint1: `Cộng tỷ lệ lạm phát của cả 5 năm rồi chia 5.`,
    hint2: `(2.5 + 3.2 + 1.8 + 4.5 + 3.0) / 5.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'average']
  },
  {
    id: 'dgnl_data_30',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Biến động Lạm phát và Lãi suất tiết kiệm (2019 - 2023)',
    stem: `Giả sử đầu năm 2022, ông A gửi tiết kiệm 1 tỷ VNĐ tại ngân hàng với kỳ hạn 1 năm. Tuy nhiên, đến cuối năm 2022, do tình hình lạm phát cao như biểu đồ đã thể hiện, sức mua thực tế của số tiền lãi mà ông A nhận được đã bị xói mòn. Tính giá trị "Lãi thực" (số tiền lãi thực tế quy đổi theo sức mua) mà ông A nhận được trong năm 2022 là bao nhiêu triệu VNĐ?`,
    choices: [
      { id: 'A', text: '30 triệu VNĐ.' },
      { id: 'B', text: '45 triệu VNĐ.' },
      { id: 'C', text: '75 triệu VNĐ.' },
      { id: 'D', text: '105 triệu VNĐ.' }
    ],
    answer: 'A',
    solution: `Năm 2022: Lãi suất tiết kiệm = $7,5\%$
Tỷ lệ Lạm phát = $4,5\%$
Lãi suất thực = $7,5\% - 4,5\% = 3\%$.
Giá trị Lãi thực = Tiền gốc $\times$ Lãi suất thực = $1 \text{ tỷ} \times 3\% = 30$ triệu VNĐ.

*(Hoặc tính cách 2: Lãi danh nghĩa = 75 triệu, Lạm phát "ăn" mất 45 triệu, phần còn lại có ý nghĩa tăng sức mua là 30 triệu).*

**Chọn A.***`,
    hint1: `Tính lãi suất thực của năm 2022 rồi nhân với số tiền gốc.`,
    hint2: `Lãi suất thực 2022 là 3%.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'real-interest']
  },
  {
    id: 'dgnl_data_31',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Biến động Lạm phát và Lãi suất tiết kiệm (2019 - 2023)',
    stem: `Năm 2024, Ngân hàng Nhà nước dự báo Tỷ lệ lạm phát sẽ được kiểm soát và giảm xuống mức 2,5%. Để đảm bảo "Lãi suất thực" của người gửi tiền trong năm 2024 được duy trì bằng đúng mức Lãi suất thực của năm 2023, các ngân hàng thương mại cần thiết lập mức Lãi suất tiết kiệm trung bình là bao nhiêu phần trăm trong năm 2024?`,
    choices: [
      { id: 'A', text: '4,0%.' },
      { id: 'B', text: '4,5%.' },
      { id: 'C', text: '5,0%.' },
      { id: 'D', text: '5,5%.' }
    ],
    answer: 'B',
    solution: `- Năm 2023: Lãi suất tiết kiệm = $5,0\%$, Lạm phát = $3,0\%$.
$\rightarrow$ Lãi suất thực 2023 = $5,0\% - 3,0\% = 2,0\%$.
- Để Lãi suất thực 2024 = $2,0\%$ trong khi Lạm phát dự báo 2024 = $2,5\%$
$\rightarrow$ Lãi suất tiết kiệm 2024 = Lãi suất thực + Lạm phát = $2,0\% + 2,5\% = 4,5\%$.

**Chọn B.***`,
    hint1: `Tính lãi suất thực năm 2023. Sau đó áp dụng cho năm 2024 cộng ngược lại với Lạm phát năm 2024 để ra Lãi suất tiết kiệm.`,
    hint2: `Lãi thực 2023 là 2.0%. 2.0% + 2.5% = 4.5%.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'projection']
  }
,
  {
    id: 'dgnl_data_32',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Sản lượng điện năng phân theo nguồn (2021-2023)',
    passageText: `Biểu đồ cột chồng dưới đây thể hiện Sản lượng điện năng (đơn vị: Tỷ kWh) được phân theo 3 nguồn: Nhiệt điện, Thủy điện, và Năng lượng tái tạo của một khu vực từ năm 2021 đến 2023.
- **Năm 2021**: Nhiệt điện 50; Thủy điện 30; Năng lượng tái tạo 10
- **Năm 2022**: Nhiệt điện 45; Thủy điện 35; Năng lượng tái tạo 20
- **Năm 2023**: Nhiệt điện 40; Thủy điện 40; Năng lượng tái tạo 40`,
    stem: `Tổng sản lượng điện của toàn khu vực trong năm 2022 là bao nhiêu tỷ kWh?`,
    choices: [
      { id: 'A', text: '90 tỷ kWh.' },
      { id: 'B', text: '100 tỷ kWh.' },
      { id: 'C', text: '110 tỷ kWh.' },
      { id: 'D', text: '120 tỷ kWh.' }
    ],
    answer: 'B',
    solution: `Năm 2022: - Nhiệt điện: 45
- Thủy điện: 35
- Năng lượng tái tạo: 20
Tổng = $45 + 35 + 20 = 100$ (tỷ kWh).

**Chọn B.***`,
    hint1: `Cộng 3 thành phần của cột năm 2022 lại.`,
    hint2: `45 + 35 + 20.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'stacked-bar']
  },
  {
    id: 'dgnl_data_33',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Sản lượng điện năng phân theo nguồn (2021-2023)',
    stem: `Trong giai đoạn 2021 - 2023, Năng lượng tái tạo đã có tốc độ tăng trưởng là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '100%.' },
      { id: 'B', text: '200%.' },
      { id: 'C', text: '300%.' },
      { id: 'D', text: '400%.' }
    ],
    answer: 'C',
    solution: `- Năng lượng tái tạo năm 2021 là 10 tỷ kWh.
- Năng lượng tái tạo năm 2023 là 40 tỷ kWh.
Tốc độ tăng trưởng = $\frac{40 - 10}{10} \times 100\% = \frac{30}{10} \times 100\% = 300\%$.

**Chọn C.***`,
    hint1: `Công thức tốc độ tăng trưởng của NL tái tạo: (Năm 2023 - Năm 2021) / Năm 2021 * 100%.`,
    hint2: `(40 - 10) / 10 = 300%.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_34',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Sản lượng điện năng phân theo nguồn (2021-2023)',
    stem: `Tỷ trọng của Thủy điện trong tổng cơ cấu sản lượng điện năm 2023 là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '25,5%.' },
      { id: 'B', text: '30,0%.' },
      { id: 'C', text: '33,3%.' },
      { id: 'D', text: '40,0%.' }
    ],
    answer: 'C',
    solution: `- Thủy điện năm 2023 là 40 tỷ kWh.
- Tổng sản lượng năm 2023 là: $40 + 40 + 40 = 120$ tỷ kWh.
Tỷ trọng = $\frac{40}{120} \times 100\% \approx 33,3\%$.

**Chọn C.***`,
    hint1: `Tính tổng sản lượng năm 2023 rồi lập tỉ số với sản lượng thủy điện.`,
    hint2: `40 / 120 = 33.3%.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'proportion']
  },
  {
    id: 'dgnl_data_35',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Sản lượng điện năng phân theo nguồn (2021-2023)',
    stem: `Giả sử năm 2024, tổng sản lượng điện đạt 150 tỷ kWh, trong đó Nhiệt điện bị cắt giảm xuống chỉ còn chiếm 20% tổng sản lượng. Sản lượng của Năng lượng tái tạo tăng lên gấp rưỡi (1,5 lần) so với năm 2023. Hỏi sản lượng Thủy điện trong năm 2024 đạt bao nhiêu tỷ kWh?`,
    choices: [
      { id: 'A', text: '45 tỷ kWh.' },
      { id: 'B', text: '50 tỷ kWh.' },
      { id: 'C', text: '60 tỷ kWh.' },
      { id: 'D', text: '65 tỷ kWh.' }
    ],
    answer: 'C',
    solution: `- Tổng sản lượng năm 2024 là 150 tỷ kWh.
- Nhiệt điện năm 2024 = $20\% \times 150 = 30$ tỷ kWh.
- NL tái tạo năm 2024 = $1,5 \times \text{NL tái tạo 2023} = 1,5 \times 40 = 60$ tỷ kWh.
- Thủy điện 2024 = Tổng - Nhiệt điện - NL tái tạo = $150 - 30 - 60 = 60$ tỷ kWh.

**Chọn C.***`,
    hint1: `Tính Nhiệt điện 2024 (20% của 150) và Năng lượng tái tạo 2024 (gấp 1.5 lần mức 40 của năm 2023).`,
    hint2: `Thủy điện = 150 - Nhiệt điện - NL tái tạo.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'projection']
  },
  {
    id: 'dgnl_data_36',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Sản lượng điện năng phân theo nguồn (2021-2023)',
    stem: `Dự báo trong năm 2024, tổng sản lượng điện năng của toàn khu vực sẽ tăng trưởng 15% so với năm 2023. Chính quyền địa phương đặt mục tiêu tỷ trọng của Năng lượng tái tạo trong năm 2024 sẽ chiếm 40% tổng sản lượng điện toàn khu vực. Hỏi sản lượng điện từ Năng lượng tái tạo trong năm 2024 cần tăng thêm bao nhiêu tỷ kWh so với năm 2023 để đạt được mục tiêu này?`,
    choices: [
      { id: 'A', text: '10,5 tỷ kWh.' },
      { id: 'B', text: '12,6 tỷ kWh.' },
      { id: 'C', text: '15,2 tỷ kWh.' },
      { id: 'D', text: '18,4 tỷ kWh.' }
    ],
    answer: 'C',
    solution: `- Tổng sản lượng điện năm 2023 là $40+40+40 = 120$ tỷ kWh.
- Tổng sản lượng năm 2024 = $120 \times (1 + 15\%) = 138$ tỷ kWh.
- Mục tiêu NL tái tạo năm 2024 = $40\% \times 138 = 55,2$ tỷ kWh.
- NL tái tạo năm 2023 là 40 tỷ kWh.
- Lượng NL tái tạo cần tăng thêm = $55,2 - 40 = 15,2$ tỷ kWh.

**Chọn C.***`,
    hint1: `Tính Tổng sản lượng năm 2024. Sau đó tính 40% của Tổng đó để ra sản lượng NL tái tạo mục tiêu.`,
    hint2: `Tổng 2024 = 120 * 1.15 = 138. Mục tiêu NL tái tạo = 138 * 40% = 55.2. Cần tăng thêm 15.2.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'optimization', 'planning']
  }
,
  {
    id: 'dgnl_data_37',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Sản lượng và thị trường xuất khẩu gạo của Quốc gia M',
    passageText: `Biểu đồ kết hợp cung cấp hai thông tin: - Biểu đồ cột thể hiện Tổng Sản lượng xuất khẩu gạo (triệu tấn) của Quốc gia M giai đoạn 2020 - 2023. Số liệu các năm lần lượt: 2020 (5); 2021 (6); 2022 (5,5); 2023 (8).
- Biểu đồ tròn thể hiện Cơ cấu thị trường xuất khẩu riêng trong năm 2023: Châu Á (50%), Châu Âu (30%), Khác (20%).`,
    stem: `Sản lượng gạo xuất khẩu sang thị trường Châu Á trong năm 2023 là bao nhiêu triệu tấn?`,
    choices: [
      { id: 'A', text: '3 triệu tấn.' },
      { id: 'B', text: '3,5 triệu tấn.' },
      { id: 'C', text: '4 triệu tấn.' },
      { id: 'D', text: '4,5 triệu tấn.' }
    ],
    answer: 'C',
    solution: `- Tổng sản lượng xuất khẩu năm 2023 là 8 triệu tấn (đọc từ biểu đồ cột).
- Tỷ trọng thị trường Châu Á năm 2023 là $50\%$ (đọc từ biểu đồ tròn).
$\rightarrow$ Sản lượng xuất khẩu sang Châu Á = $50\% \times 8 = 4$ (triệu tấn).

**Chọn C.***`,
    hint1: `Tìm tổng sản lượng năm 2023 ở biểu đồ cột. Sau đó nhân với tỷ trọng của Châu Á ở biểu đồ tròn.`,
    hint2: `Lấy 8 triệu tấn nhân 50%.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'combined-chart']
  },
  {
    id: 'dgnl_data_38',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Sản lượng và thị trường xuất khẩu gạo của Quốc gia M',
    stem: `Năm 2022, sản lượng xuất khẩu đã giảm bao nhiêu phần trăm so với năm 2021?`,
    choices: [
      { id: 'A', text: '7,14%.' },
      { id: 'B', text: '8,33%.' },
      { id: 'C', text: '9,09%.' },
      { id: 'D', text: '10,50%.' }
    ],
    answer: 'B',
    solution: `- Sản lượng năm 2021 là 6 triệu tấn.
- Sản lượng năm 2022 là 5,5 triệu tấn.
Tỷ lệ giảm = $\frac{6 - 5,5}{6} \times 100\% = \frac{0,5}{6} \times 100\% \approx 8,33\%$.

**Chọn B.***`,
    hint1: `Sử dụng công thức tính tỷ lệ giảm: (Năm trước - Năm sau) / Năm trước * 100%.`,
    hint2: `Lấy (6 - 5.5) / 6.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'decrease']
  },
  {
    id: 'dgnl_data_39',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Sản lượng và thị trường xuất khẩu gạo của Quốc gia M',
    stem: `Trung bình mỗi năm trong giai đoạn 2020 - 2023, Quốc gia M xuất khẩu bao nhiêu triệu tấn gạo?`,
    choices: [
      { id: 'A', text: '5,875 triệu tấn.' },
      { id: 'B', text: '6,125 triệu tấn.' },
      { id: 'C', text: '6,500 triệu tấn.' },
      { id: 'D', text: '7,125 triệu tấn.' }
    ],
    answer: 'B',
    solution: `Sản lượng các năm: 2020: 5
2021: 6
2022: 5,5
2023: 8
Tổng 4 năm = $5 + 6 + 5,5 + 8 = 24,5$ (triệu tấn).
Trung bình = $\frac{24,5}{4} = 6,125$ (triệu tấn).

**Chọn B.***`,
    hint1: `Cộng sản lượng 4 năm rồi chia cho 4.`,
    hint2: `24.5 / 4.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'average']
  },
  {
    id: 'dgnl_data_40',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Sản lượng và thị trường xuất khẩu gạo của Quốc gia M',
    stem: `Giả sử năm 2024, tổng sản lượng xuất khẩu dự kiến tăng 15% so với năm 2023. Tuy nhiên, do một số khó khăn, sản lượng xuất khẩu sang Châu Á giữ nguyên như năm 2023. Tỷ trọng xuất khẩu gạo sang Châu Á trong năm 2024 sẽ là bao nhiêu?`,
    choices: [
      { id: 'A', text: '35,5%.' },
      { id: 'B', text: '40,2%.' },
      { id: 'C', text: '43,5%.' },
      { id: 'D', text: '50,0%.' }
    ],
    answer: 'C',
    solution: `- Tổng sản lượng năm 2024 = Tổng sản lượng 2023 $\times (1 + 15\%) = 8 \times 1,15 = 9,2$ (triệu tấn).
- Sản lượng sang Châu Á 2024 = Sản lượng sang Châu Á 2023 = $4$ (triệu tấn).
Tỷ trọng mới = $\frac{4}{9,2} \times 100\% \approx 43,478\% \approx 43,5\%$.

**Chọn C.***`,
    hint1: `Tính tổng sản lượng năm 2024 (tăng 15% so với 8 triệu). Khối lượng Châu Á vẫn là 4 triệu.`,
    hint2: `Tổng 2024 là 9.2 triệu tấn. Lập tỉ số 4 / 9.2.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'proportion', 'projection']
  },
  {
    id: 'dgnl_data_41',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Sản lượng và thị trường xuất khẩu gạo của Quốc gia M',
    stem: `Quốc gia M đặt mục tiêu đến năm 2025, Tổng sản lượng xuất khẩu gạo phải đạt 10 triệu tấn. Nhằm đa dạng hóa thị trường và hạn chế rủi ro tập trung, chính phủ chủ trương kiểm soát sao cho tỷ trọng sản lượng xuất khẩu sang Châu Á trong năm 2025 không được vượt quá 35% tổng sản lượng của năm đó. Để tuân thủ chặt chẽ quy định này, sản lượng xuất khẩu gạo sang Châu Á trong năm 2025 sẽ giảm đi bao nhiêu triệu tấn so với mức xuất khẩu thực tế của thị trường này trong năm 2023?`,
    choices: [
      { id: 'A', text: '0,25 triệu tấn.' },
      { id: 'B', text: '0,50 triệu tấn.' },
      { id: 'C', text: '0,75 triệu tấn.' },
      { id: 'D', text: '1,00 triệu tấn.' }
    ],
    answer: 'B',
    solution: `- Tổng sản lượng năm 2025 là 10 triệu tấn.
- Tỷ trọng tối đa của Châu Á năm 2025 là $35\%$.
$\rightarrow$ Sản lượng tối đa sang Châu Á 2025 = $35\% \times 10 = 3,5$ (triệu tấn).
- Thực tế năm 2023, sản lượng sang Châu Á là $4$ (triệu tấn).
$\rightarrow$ Mức sụt giảm là: $4 - 3,5 = 0,5$ (triệu tấn).

**Chọn B.***`,
    hint1: `Tính sản lượng tối đa sang Châu Á năm 2025 là 35% của 10 triệu tấn. Rồi so sánh với năm 2023 (4 triệu tấn).`,
    hint2: `Châu Á 2025 = 3.5. Giảm = 4 - 3.5 = 0.5.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'optimization', 'planning']
  }
,
  {
    id: 'dgnl_data_42',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Lượng Khách du lịch quốc tế và Cơ cấu Châu lục',
    passageText: `Biểu đồ kết hợp cung cấp hai thông tin: - Biểu đồ cột thể hiện Số lượng Khách du lịch quốc tế (Triệu lượt) đến Quốc gia N giai đoạn 2019 - 2023. Số liệu các năm: 2019 (18); 2020 (3); 2021 (1); 2022 (12); 2023 (20).
- Biểu đồ tròn thể hiện Cơ cấu Khách du lịch theo Châu lục riêng trong năm 2023: Châu Á (60%), Châu Âu (25%), Châu Mỹ (10%), Khác (5%).`,
    stem: `Lượng khách du lịch quốc tế đến Quốc gia N năm 2020 đã giảm bao nhiêu triệu lượt so với năm 2019 (giai đoạn bùng phát dịch bệnh)?`,
    choices: [
      { id: 'A', text: '12 triệu lượt.' },
      { id: 'B', text: '13 triệu lượt.' },
      { id: 'C', text: '15 triệu lượt.' },
      { id: 'D', text: '18 triệu lượt.' }
    ],
    answer: 'C',
    solution: `- Năm 2019: 18 triệu lượt.
- Năm 2020: 3 triệu lượt.
Số lượng giảm = $18 - 3 = 15$ (triệu lượt).

**Chọn C.***`,
    hint1: `Tìm dữ liệu của năm 2019 và 2020 rồi trừ cho nhau.`,
    hint2: `18 - 3.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'combined-chart']
  },
  {
    id: 'dgnl_data_43',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Lượng Khách du lịch quốc tế và Cơ cấu Châu lục',
    stem: `Số lượng khách du lịch đến từ Châu Á trong năm 2023 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '10 triệu lượt.' },
      { id: 'B', text: '12 triệu lượt.' },
      { id: 'C', text: '14 triệu lượt.' },
      { id: 'D', text: '15 triệu lượt.' }
    ],
    answer: 'B',
    solution: `- Tổng số khách năm 2023 là 20 triệu lượt (đọc từ biểu đồ cột).
- Tỷ trọng khách từ Châu Á là $60\%$ (đọc từ biểu đồ tròn).
Số lượng khách Châu Á = $60\% \times 20 = 12$ (triệu lượt).

**Chọn B.***`,
    hint1: `Nhân tổng số khách năm 2023 với tỷ lệ phần trăm của Châu Á.`,
    hint2: `20 triệu lượt * 60%.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'combined-chart']
  },
  {
    id: 'dgnl_data_44',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Lượng Khách du lịch quốc tế và Cơ cấu Châu lục',
    stem: `Số lượng khách du lịch đến từ Châu Âu nhiều gấp bao nhiêu lần số lượng khách đến từ Châu Mỹ trong năm 2023?`,
    choices: [
      { id: 'A', text: '1,5 lần.' },
      { id: 'B', text: '2,0 lần.' },
      { id: 'C', text: '2,5 lần.' },
      { id: 'D', text: '3,0 lần.' }
    ],
    answer: 'C',
    solution: `Do hai đối tượng nằm trong cùng một tổng thể (cùng năm 2023), ta có thể lấy trực tiếp tỷ lệ phần trăm chia cho nhau để ra số lần gấp: - Tỷ lệ Châu Âu = $25\%$.
- Tỷ lệ Châu Mỹ = $10\%$.
Gấp = $\frac{25\%}{10\%} = 2,5$ (lần).

**Chọn C.***`,
    hint1: `Vì chung một năm, chỉ cần lấy phần trăm của Châu Âu chia cho phần trăm của Châu Mỹ.`,
    hint2: `25% / 10% = 2.5.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'ratio']
  },
  {
    id: 'dgnl_data_45',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Lượng Khách du lịch quốc tế và Cơ cấu Châu lục',
    stem: `Nếu giả sử năm 2024 lượng khách du lịch không phải từ Châu Á (tức là Châu Âu, Châu Mỹ và Khác) tăng thêm 20% so với con số của năm 2023, trong khi lượng khách từ Châu Á giữ nguyên. Tổng lượng khách du lịch của Quốc gia N năm 2024 sẽ đạt bao nhiêu triệu lượt?`,
    choices: [
      { id: 'A', text: '20,6 triệu lượt.' },
      { id: 'B', text: '21,0 triệu lượt.' },
      { id: 'C', text: '21,6 triệu lượt.' },
      { id: 'D', text: '22,0 triệu lượt.' }
    ],
    answer: 'C',
    solution: `- Tổng lượng khách 2023 là 20 triệu.
- Khách Châu Á 2023 = $12$ triệu (đã tính ở câu trước). Năm 2024 giữ nguyên = $12$ triệu.
- Khách Không phải Châu Á 2023 = $20 - 12 = 8$ (triệu lượt).
- Khách Không phải Châu Á 2024 (tăng $20\%$) = $8 \times (1 + 20\%) = 9,6$ (triệu lượt).
- Tổng lượng khách 2024 = $12 + 9,6 = 21,6$ (triệu lượt).

**Chọn C.***`,
    hint1: `Tìm lượng khách không phải Châu Á trong năm 2023 (là 40% của 20 triệu). Tính mức tăng 20% của nhóm này.`,
    hint2: `Nhóm còn lại = 8 triệu. Tăng 20% thành 9.6. Cộng với Châu Á 12 = 21.6.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'projection']
  },
  {
    id: 'dgnl_data_46',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Lượng Khách du lịch quốc tế và Cơ cấu Châu lục',
    stem: `Giả sử chi tiêu bình quân của một khách du lịch đến từ Châu Âu trong năm 2023 là 1500 USD, và của một khách đến từ Châu Mỹ là 2000 USD. Tổng doanh thu thu được từ lượng khách của hai châu lục này trong năm 2023 là bao nhiêu tỷ USD?`,
    choices: [
      { id: 'A', text: '10,5 tỷ USD.' },
      { id: 'B', text: '11,5 tỷ USD.' },
      { id: 'C', text: '12,0 tỷ USD.' },
      { id: 'D', text: '12,5 tỷ USD.' }
    ],
    answer: 'B',
    solution: `- Số lượng khách Châu Âu = $25\% \times 20$ triệu = 5 triệu (lượt).
$\rightarrow$ Doanh thu từ Châu Âu = $5.000.000 \times 1500 = 7.500.000.000$ USD = 7,5 (tỷ USD).
- Số lượng khách Châu Mỹ = $10\% \times 20$ triệu = 2 triệu (lượt).
$\rightarrow$ Doanh thu từ Châu Mỹ = $2.000.000 \times 2000 = 4.000.000.000$ USD = 4,0 (tỷ USD).
- Tổng doanh thu = $7,5 + 4,0 = 11,5$ (tỷ USD).

**Chọn B.***`,
    hint1: `Tính chính xác số lượng khách (đơn vị triệu) của từng Châu Lục, sau đó nhân với chi tiêu bình quân của mỗi khách.`,
    hint2: `Châu Âu là 5 triệu người * 1500 USD. Châu Mỹ là 2 triệu người * 2000 USD. Lưu ý quy đổi ra tỷ USD (1 tỷ = 1000 triệu).`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'revenue']
  }
,
  {
    id: 'dgnl_data_47',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Kết quả sản xuất kinh doanh 3 phân xưởng A, B, C (2024)',
    passageText: `Bảng thống kê kết quả sản xuất kinh doanh của 3 phân xưởng (A, B, C) trong Tập đoàn Y năm 2024. Biết rằng Năng suất lao động (NSLĐ) = Tổng doanh thu / Số lượng nhân sự.
- **Xưởng A**: Doanh thu 320 tỷ; Nhân sự 100 người; Biên lợi nhuận gộp 15%
- **Xưởng B**: Doanh thu 480 tỷ; Nhân sự 120 người; Biên lợi nhuận gộp 25%
- **Xưởng C**: Doanh thu 200 tỷ; Nhân sự 80 người; Biên lợi nhuận gộp 10%`,
    stem: `Năng suất lao động của Xưởng B cao gấp bao nhiêu lần Năng suất lao động của Xưởng C?`,
    choices: [
      { id: 'A', text: '1,2 lần.' },
      { id: 'B', text: '1,5 lần.' },
      { id: 'C', text: '1,6 lần.' },
      { id: 'D', text: '2,0 lần.' }
    ],
    answer: 'C',
    solution: `- Năng suất lao động của Xưởng B = $\frac{480}{120} = 4$ (Tỷ đồng/người).
- Năng suất lao động của Xưởng C = $\frac{200}{80} = 2,5$ (Tỷ đồng/người).
Tỷ lệ = $\frac{4}{2,5} = 1,6$ (lần).

**Chọn C.***`,
    hint1: `Tính năng suất của Xưởng B (Doanh thu B / Nhân sự B) và Xưởng C (Doanh thu C / Nhân sự C).`,
    hint2: `4 chia cho 2.5 = 1.6.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_48',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Kết quả sản xuất kinh doanh 3 phân xưởng A, B, C (2024)',
    stem: `Tổng lợi nhuận gộp của toàn bộ Tập đoàn Y trong năm 2024 là bao nhiêu tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '150 tỷ.' },
      { id: 'B', text: '165 tỷ.' },
      { id: 'C', text: '188 tỷ.' },
      { id: 'D', text: '210 tỷ.' }
    ],
    answer: 'C',
    solution: `- Lợi nhuận gộp Xưởng A = $320 \times 15\% = 48$ tỷ.
- Lợi nhuận gộp Xưởng B = $480 \times 25\% = 120$ tỷ.
- Lợi nhuận gộp Xưởng C = $200 \times 10\% = 20$ tỷ.
Tổng lợi nhuận gộp Tập đoàn = $48 + 120 + 20 = 188$ tỷ.

**Chọn C.***`,
    hint1: `Tính lợi nhuận của từng Xưởng bằng cách lấy Doanh thu nhân với Biên lợi nhuận rồi cộng lại.`,
    hint2: `48 + 120 + 20.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'profit']
  },
  {
    id: 'dgnl_data_49',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Kết quả sản xuất kinh doanh 3 phân xưởng A, B, C (2024)',
    stem: `Biên lợi nhuận gộp trung bình của toàn Tập đoàn Y năm 2024 là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '16,7%.' },
      { id: 'B', text: '18,0%.' },
      { id: 'C', text: '18,8%.' },
      { id: 'D', text: '20,5%.' }
    ],
    answer: 'C',
    solution: `- Tổng Lợi nhuận gộp của Tập đoàn Y (như đã tính) = 188 tỷ.
- Tổng Doanh thu của Tập đoàn Y = $320 + 480 + 200 = 1000$ tỷ.
Biên lợi nhuận gộp trung bình = $\frac{\text{Tổng lợi nhuận}}{\text{Tổng doanh thu}} \times 100\% = \frac{188}{1000} \times 100\% = 18,8\%$.

**Chọn C.***`,
    hint1: `Dùng Tổng lợi nhuận chia cho Tổng doanh thu của toàn Tập đoàn. Không được lấy trung bình cộng 3 tỷ lệ phần trăm.`,
    hint2: `188 / 1000 = 18.8%.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'profit-margin']
  },
  {
    id: 'dgnl_data_50',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Kết quả sản xuất kinh doanh 3 phân xưởng A, B, C (2024)',
    stem: `Giả sử năm 2025, Xưởng A được đầu tư công nghệ mới giúp năng suất lao động tăng 25% nhưng số lượng nhân sự lại bị cắt giảm 10%. Doanh thu của Xưởng A trong năm 2025 sẽ thay đổi như thế nào so với năm 2024?`,
    choices: [
      { id: 'A', text: 'Tăng 10%.' },
      { id: 'B', text: 'Tăng 12,5%.' },
      { id: 'C', text: 'Tăng 15%.' },
      { id: 'D', text: 'Không đổi.' }
    ],
    answer: 'B',
    solution: `- Năng suất lao động năm 2024 của Xưởng A = $\frac{320}{100} = 3,2$.
- Năng suất năm 2025 tăng $25\% \rightarrow$ $3,2 \times 1,25 = 4,0$.
- Số nhân sự năm 2025 bị cắt giảm $10\% \rightarrow$ $100 \times 0,9 = 90$ (người).
- Doanh thu năm 2025 = Năng suất $\times$ Nhân sự = $4,0 \times 90 = 360$ (tỷ).
Doanh thu năm 2024 là 320 tỷ.
Tốc độ tăng trưởng doanh thu = $\frac{360 - 320}{320} \times 100\% = 12,5\%$.

*Cách 2 (nhanh hơn)*: $1,25 \times 0,9 = 1,125 \rightarrow$ Tăng 12,5%.

**Chọn B.***`,
    hint1: `Tìm năng suất và nhân sự mới của năm 2025, nhân lại với nhau để ra Doanh thu mới. Rồi so sánh với Doanh thu cũ (320 tỷ).`,
    hint2: `Doanh thu mới = 360. (360 - 320) / 320 = 12.5%.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'projection']
  },
  {
    id: 'dgnl_data_51',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Kết quả sản xuất kinh doanh 3 phân xưởng A, B, C (2024)',
    stem: `(Bài toán tối ưu hóa cơ cấu) Sang năm 2026, do Xưởng C làm ăn kém hiệu quả, Tập đoàn quyết định đóng cửa hoàn toàn Xưởng C (Doanh thu xưởng C bằng 0). Tập đoàn đặt mục tiêu: Dù mất đi Xưởng C, Tổng lợi nhuận gộp của toàn Tập đoàn vẫn phải bằng đúng năm 2024 (tức là 188 tỷ). Nếu giả sử Biên lợi nhuận gộp của Xưởng A và B không thay đổi, và Doanh thu của Xưởng A được giữ nguyên ở mức 320 tỷ, thì Doanh thu của Xưởng B cần đạt bao nhiêu tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '480 tỷ.' },
      { id: 'B', text: '500 tỷ.' },
      { id: 'C', text: '520 tỷ.' },
      { id: 'D', text: '560 tỷ.' }
    ],
    answer: 'D',
    solution: `- Tổng Lợi nhuận gộp mục tiêu = 188 tỷ.
- Xưởng C đóng cửa nên Lợi nhuận = 0.
- Xưởng A giữ nguyên Doanh thu 320 tỷ và biên Lợi nhuận 15% $\rightarrow$ Lợi nhuận A = 48 tỷ.
$\rightarrow$ Lợi nhuận gộp mà Xưởng B cần đạt = $188 - 48 = 140$ (tỷ).
Biên lợi nhuận gộp Xưởng B = 25%.
$\rightarrow$ Doanh thu Xưởng B cần đạt = $\frac{140}{25\%} = 560$ (tỷ VNĐ).

**Chọn D.***`,
    hint1: `Tìm khoản lợi nhuận mà Xưởng B phải gánh (bằng Tổng 188 tỷ trừ đi lợi nhuận Xưởng A là 48 tỷ).`,
    hint2: `Lợi nhuận B = 140. Doanh thu B = 140 / 25% = 560.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'optimization']
  },
  {
    id: 'dgnl_data_52',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Chỉ số tài chính 4 dự án kinh doanh (P1, P2, P3, P4)',
    passageText: `Bảng dưới đây thống kê các thông số tài chính dự kiến của 4 dự án kinh doanh (P1, P2, P3, P4). Biết rằng: - Lợi nhuận dự kiến = (Giá bán 1 SP - Chi phí biến đổi 1 SP) × Số lượng SP - Chi phí cố định.
- Điểm hòa vốn (Số SP) = Chi phí cố định / (Giá bán 1 SP - Chi phí biến đổi 1 SP).
Số liệu các dự án (Cố định, Biến đổi/SP, Giá bán/SP, Số SP dự kiến): - **P1**: 100; 2,0; 3,0; 500
- **P2**: 200; 1,5; 2,5; 1000
- **P3**: 150; 3,0; 4,0; 400
- **P4**: 50; 1,0; 1,8; 800`,
    stem: `Dự án nào mang lại mức lợi nhuận dự kiến cao nhất?`,
    choices: [
      { id: 'A', text: 'P1.' },
      { id: 'B', text: 'P2.' },
      { id: 'C', text: 'P3.' },
      { id: 'D', text: 'P4.' }
    ],
    answer: 'B',
    solution: `Tính lợi nhuận dự kiến cho từng dự án: $\text{LN} = (\text{Giá} - \text{Biến}) \times \text{Số SP} - \text{Cố định}$
- P1: $(3,0 - 2,0) \times 500 - 100 = 1,0 \times 500 - 100 = 400$
- P2: $(2,5 - 1,5) \times 1000 - 200 = 1,0 \times 1000 - 200 = 800$
- P3: $(4,0 - 3,0) \times 400 - 150 = 1,0 \times 400 - 150 = 250$
- P4: $(1,8 - 1,0) \times 800 - 50 = 0,8 \times 800 - 50 = 590$
Dự án P2 mang lại lợi nhuận cao nhất (800).

**Chọn B.***`,
    hint1: `Sử dụng công thức tính lợi nhuận dự kiến đã cho trong đề bài và thay số cho từng dự án.`,
    hint2: `P2 = 1.0 * 1000 - 200 = 800.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'data-table', 'profit']
  },
  {
    id: 'dgnl_data_53',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Chỉ số tài chính 4 dự án kinh doanh (P1, P2, P3, P4)',
    stem: `Số sản phẩm tối thiểu mà dự án P3 cần bán ra để đạt Điểm hòa vốn (không bị lỗ) là bao nhiêu?`,
    choices: [
      { id: 'A', text: '100 sản phẩm.' },
      { id: 'B', text: '150 sản phẩm.' },
      { id: 'C', text: '200 sản phẩm.' },
      { id: 'D', text: '250 sản phẩm.' }
    ],
    answer: 'B',
    solution: `Công thức Điểm hòa vốn = Chi phí cố định / (Giá bán 1 SP - Chi phí biến đổi 1 SP).
Với dự án P3: Chi phí cố định = 150. Giá bán = 4,0. Chi phí biến đổi = 3,0.
Điểm hòa vốn = $\frac{150}{4,0 - 3,0} = \frac{150}{1} = 150$ (sản phẩm).

**Chọn B.***`,
    hint1: `Áp dụng công thức Điểm hòa vốn đã cho.`,
    hint2: `150 / (4.0 - 3.0) = 150.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'breakeven']
  },
  {
    id: 'dgnl_data_54',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Chỉ số tài chính 4 dự án kinh doanh (P1, P2, P3, P4)',
    stem: `Giả sử do biến động thị trường, số lượng sản phẩm thực tế bán được của dự án P1 thấp hơn 20% so với dự kiến. Lợi nhuận thực tế của dự án P1 khi đó là bao nhiêu?`,
    choices: [
      { id: 'A', text: '200 Tr. VNĐ.' },
      { id: 'B', text: '250 Tr. VNĐ.' },
      { id: 'C', text: '300 Tr. VNĐ.' },
      { id: 'D', text: '350 Tr. VNĐ.' }
    ],
    answer: 'C',
    solution: `- Số lượng sản phẩm dự kiến của P1 = 500.
- Số lượng thực tế = $500 \times (1 - 20\%) = 400$ (sản phẩm).
- Lợi nhuận thực tế P1 = $(3,0 - 2,0) \times 400 - 100 = 1,0 \times 400 - 100 = 300$ (Tr. VNĐ).

**Chọn C.***`,
    hint1: `Tính số sản phẩm thực tế (giảm 20% so với 500). Sau đó tính lại lợi nhuận với số sản phẩm mới.`,
    hint2: `Số sản phẩm mới = 400. LN = 400 * 1.0 - 100 = 300.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'projection']
  },
  {
    id: 'dgnl_data_55',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Chỉ số tài chính 4 dự án kinh doanh (P1, P2, P3, P4)',
    stem: `Tỷ suất lợi nhuận trên tổng chi phí (ROI) dự kiến của dự án P4 xấp xỉ bằng bao nhiêu phần trăm? (Biết: Tổng chi phí = Chi phí cố định + Tổng Chi phí biến đổi).`,
    choices: [
      { id: 'A', text: '50,5%.' },
      { id: 'B', text: '60,0%.' },
      { id: 'C', text: '69,4%.' },
      { id: 'D', text: '75,2%.' }
    ],
    answer: 'C',
    solution: `- Với dự án P4: Số SP dự kiến = 800.
- Tổng chi phí biến đổi = $800 \times 1,0 = 800$.
- Chi phí cố định = $50$.
- Tổng chi phí = $800 + 50 = 850$.
- Lợi nhuận P4 = $590$ (đã tính ở Câu 1).
- Tỷ suất ROI = $\frac{\text{Lợi nhuận}}{\text{Tổng chi phí}} \times 100\% = \frac{590}{850} \times 100\% \approx 69,41\%$.

**Chọn C.***`,
    hint1: `Tính Tổng chi phí của P4 (gồm chi phí cố định + tổng chi phí biến đổi). Sau đó dùng lợi nhuận (590) chia cho Tổng chi phí.`,
    hint2: `Tổng chi phí = 50 + 1.0 * 800 = 850. Lập tỉ số 590 / 850.`,
    estimatedSeconds: 35,
    tags: ['data_analysis', 'dgnl', 'ratio']
  },
  {
    id: 'dgnl_data_56',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Chỉ số tài chính 4 dự án kinh doanh (P1, P2, P3, P4)',
    stem: `Giả sử dự án P2 muốn tăng Lợi nhuận dự kiến thêm 20% so với mức lợi nhuận ban đầu (tính ở điều kiện dự kiến) mà không thay đổi Giá bán và cơ cấu Chi phí. Số lượng sản phẩm tối thiểu mà dự án P2 cần bán ra là bao nhiêu?`,
    choices: [
      { id: 'A', text: '1100 sản phẩm.' },
      { id: 'B', text: '1160 sản phẩm.' },
      { id: 'C', text: '1200 sản phẩm.' },
      { id: 'D', text: '1250 sản phẩm.' }
    ],
    answer: 'B',
    solution: `- Lợi nhuận dự kiến ban đầu của P2 = $800$ (tính ở Câu 1).
- Lợi nhuận mục tiêu mới (tăng $20\%$) = $800 \times 1,2 = 960$.
- Gọi $x$ là số sản phẩm cần bán. Ta có phương trình: $(2,5 - 1,5) \times x - 200 = 960$
$\Leftrightarrow 1,0 \times x = 1160$
$\Leftrightarrow x = 1160$ (sản phẩm).

**Chọn B.***`,
    hint1: `Tính lợi nhuận mục tiêu mới (tăng 20% so với 800 là 960). Viết phương trình lợi nhuận với ẩn là Số lượng sản phẩm.`,
    hint2: `1.0 * x - 200 = 960 => x = 1160.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'optimization']
  }
,
  {
    id: 'dgnl_data_57',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Khảo sát nhân khẩu học 3 khu vực (A, B, C)',
    passageText: `Bảng dữ liệu khảo sát nhân khẩu học của 3 khu vực (A, B, C) vào năm 2023. Biết: Mật độ dân số (người/km²) = Số dân / Diện tích.
\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "A",
      "Số dân (Triệu người)": 20,
      "Tỷ lệ tăng TN (%)": 1.2,
      "Mật độ dân số (Người/km²)": "?(1)",
      "Diện tích (km²)": 50
    },
    {
      "name": "B",
      "Số dân (Triệu người)": "?(2)",
      "Tỷ lệ tăng TN (%)": 0.8,
      "Mật độ dân số (Người/km²)": 200,
      "Diện tích (km²)": 150
    },
    {
      "name": "C",
      "Số dân (Triệu người)": 10,
      "Tỷ lệ tăng TN (%)": 1.5,
      "Mật độ dân số (Người/km²)": 100,
      "Diện tích (km²)": "?(3)"
    },
    {
      "name": "Tổng cộng",
      "Số dân (Triệu người)": 60,
      "Tỷ lệ tăng TN (%)": "-",
      "Mật độ dân số (Người/km²)": "-",
      "Diện tích (km²)": "?(4)"
    }
  ],
  "options": {
    "bars": [
      "Số dân (Triệu người)",
      "Tỷ lệ tăng TN (%)",
      "Mật độ dân số (Người/km²)",
      "Diện tích (km²)"
    ]
  }
}
\`\`\``,
    stem: `Diện tích của khu vực C là bao nhiêu km²?`,
    choices: [
      { id: 'A', text: '80.000 km².' },
      { id: 'B', text: '100.000 km².' },
      { id: 'C', text: '120.000 km².' },
      { id: 'D', text: '150.000 km².' }
    ],
    answer: 'B',
    solution: `- Khu vực C có Mật độ dân số = 100 người/km².
- Số dân Khu vực C = 10 triệu người = 10.000.000 người.
- Diện tích = Số dân / Mật độ = $\frac{10.000.000}{100} = 100.000$ (km²).

**Chọn B.***`,
    hint1: `Sử dụng công thức Diện tích = Số dân / Mật độ. Đổi 10 triệu người ra số đầy đủ.`,
    hint2: `10,000,000 / 100 = 100,000.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'data-table', 'density']
  },
  {
    id: 'dgnl_data_58',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Khảo sát nhân khẩu học 3 khu vực (A, B, C)',
    stem: `Số dân của khu vực B là bao nhiêu triệu người?`,
    choices: [
      { id: 'A', text: '25 triệu người.' },
      { id: 'B', text: '30 triệu người.' },
      { id: 'C', text: '35 triệu người.' },
      { id: 'D', text: '40 triệu người.' }
    ],
    answer: 'B',
    solution: `**Cách 1: ** Tính thông qua tổng số dân.
Tổng dân số = 60 triệu. Số dân A = 20 triệu. Số dân C = 10 triệu.
Số dân B = $60 - 20 - 10 = 30$ (triệu người).

**Cách 2: ** Tính thông qua Mật độ.
Mật độ B = 200 người/km². Diện tích = 150.000 km².
Số dân B = $200 \times 150.000 = 30.000.000$ người = 30 triệu người.

**Chọn B.***`,
    hint1: `Có thể lấy Tổng dân số trừ đi dân số A và C.`,
    hint2: `60 - 20 - 10 = 30.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_59',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Khảo sát nhân khẩu học 3 khu vực (A, B, C)',
    stem: `Mật độ dân số của khu vực A cao gấp bao nhiêu lần mật độ dân số của khu vực C?`,
    choices: [
      { id: 'A', text: '2 lần.' },
      { id: 'B', text: '3 lần.' },
      { id: 'C', text: '4 lần.' },
      { id: 'D', text: '5 lần.' }
    ],
    answer: 'C',
    solution: `- Mật độ khu vực C = 100 (đề cho).
- Tính mật độ khu vực A: Số dân = 20 triệu, Diện tích = 50.000 km².
Mật độ A = $\frac{20.000.000}{50.000} = 400$ (người/km²).
- Gấp số lần = $\frac{400}{100} = 4$ (lần).

**Chọn C.***`,
    hint1: `Tính Mật độ khu vực A (20 triệu chia cho 50.000).`,
    hint2: `400 / 100 = 4.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'density']
  },
  {
    id: 'dgnl_data_60',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Khảo sát nhân khẩu học 3 khu vực (A, B, C)',
    stem: `Dự báo năm 2024, nếu số dân khu vực A chỉ biến đổi dựa trên tỷ lệ tăng tự nhiên (không có di cư), thì dân số khu vực A sẽ đạt bao nhiêu triệu người?`,
    choices: [
      { id: 'A', text: '20,12 triệu người.' },
      { id: 'B', text: '20,24 triệu người.' },
      { id: 'C', text: '21,20 triệu người.' },
      { id: 'D', text: '24,00 triệu người.' }
    ],
    answer: 'B',
    solution: `- Số dân A năm 2023 = 20 triệu người.
- Tỷ lệ tăng tự nhiên = $1,2\%$.
- Dân số năm 2024 = $20 \times (1 + 1,2\%) = 20 \times 1,012 = 20,24$ (triệu người).

**Chọn B.***`,
    hint1: `Nhân dân số cũ với (1 + tỷ lệ tăng trưởng).`,
    hint2: `20 * 1.012 = 20.24.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_61',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Khảo sát nhân khẩu học 3 khu vực (A, B, C)',
    stem: `Giả sử trong giai đoạn từ cuối năm 2023 đến cuối năm 2025, tỷ lệ tăng tự nhiên của khu vực B luôn duy trì ở mức 0,8%/năm. Đồng thời, do chính sách thu hút lao động, mỗi năm khu vực B tiếp nhận thêm đúng 100.000 người nhập cư (người nhập cư được cộng vào dân số cuối mỗi năm). Tổng dân số của khu vực B vào cuối năm 2025 xấp xỉ bằng bao nhiêu triệu người?`,
    choices: [
      { id: 'A', text: '30,48 triệu người.' },
      { id: 'B', text: '30,58 triệu người.' },
      { id: 'C', text: '30,68 triệu người.' },
      { id: 'D', text: '30,88 triệu người.' }
    ],
    answer: 'C',
    solution: `- Cuối năm 2023: Dân số B = 30 triệu người (đã tính ở câu trước). Lượng nhập cư = 0,1 triệu.
- Cuối năm 2024: $30 \times (1 + 0,8\%) + 0,1 = 30,24 + 0,1 = 30,34$ (triệu).
- Cuối năm 2025: $30,34 \times (1 + 0,8\%) + 0,1 = 30,58272 + 0,1 \approx 30,68$ (triệu người).

**Chọn C.***`,
    hint1: `Tính dân số cuối năm 2024 trước, bao gồm lượng tăng tự nhiên và nhập cư. Lấy số đó làm gốc tính cho năm 2025.`,
    hint2: `Năm 2024: 30 * 1.008 + 0.1 = 30.34. Năm 2025: 30.34 * 1.008 + 0.1.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'projection']
  },
  {
    id: 'dgnl_data_62',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Sản xuất và tiêu thụ Nông sản tỉnh X (2024)',
    passageText: `Bảng thống kê tình hình sản xuất và tiêu thụ của 3 loại nông sản chính (Gạo, Cà phê, Cao su) tại tỉnh X trong năm 2024. Một số dữ liệu đã bị khuyết. Giả định: Sản lượng = Xuất khẩu + Tiêu thụ nội địa.
\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Gạo",
      "Sản lượng (nghìn tấn)": 1500,
      "Xuất khẩu (nghìn tấn)": "?",
      "Tiêu thụ nội địa": 600,
      "Tỷ trọng XK (%)": "?"
    },
    {
      "name": "Cà phê",
      "Sản lượng (nghìn tấn)": "?",
      "Xuất khẩu (nghìn tấn)": 240,
      "Tiêu thụ nội địa": "?",
      "Tỷ trọng XK (%)": 80
    },
    {
      "name": "Cao su",
      "Sản lượng (nghìn tấn)": 120,
      "Xuất khẩu (nghìn tấn)": "?",
      "Tiêu thụ nội địa": 12,
      "Tỷ trọng XK (%)": "?"
    },
    {
      "name": "Tổng",
      "Sản lượng (nghìn tấn)": "?",
      "Xuất khẩu (nghìn tấn)": "?",
      "Tiêu thụ nội địa": "?",
      "Tỷ trọng XK (%)": "?"
    }
  ],
  "options": {
    "bars": [
      "Sản lượng (nghìn tấn)",
      "Xuất khẩu (nghìn tấn)",
      "Tiêu thụ nội địa",
      "Tỷ trọng XK (%)"
    ]
  }
}
\`\`\``,
    stem: `Tổng Khối lượng xuất khẩu của Gạo và Cao su trong năm 2024 là bao nhiêu nghìn tấn?`,
    choices: [
      { id: 'A', text: '960 nghìn tấn.' },
      { id: 'B', text: '980 nghìn tấn.' },
      { id: 'C', text: '1008 nghìn tấn.' },
      { id: 'D', text: '1012 nghìn tấn.' }
    ],
    answer: 'C',
    solution: `- Gạo: Sản lượng 1500, Nội địa 600 $\rightarrow$ Xuất khẩu = $1500 - 600 = 900$ (nghìn tấn).
- Cao su: Sản lượng 120, Nội địa 12 $\rightarrow$ Xuất khẩu = $120 - 12 = 108$ (nghìn tấn).
- Tổng = $900 + 108 = 1008$ (nghìn tấn).

**Chọn C.***`,
    hint1: `Lấy Sản lượng trừ Tiêu thụ nội địa để ra Xuất khẩu của từng mặt hàng.`,
    hint2: `Gạo XK = 900, Cao su XK = 108.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_63',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Sản xuất và tiêu thụ Nông sản tỉnh X (2024)',
    stem: `Sản lượng Cà phê trong năm 2024 cao hơn Sản lượng Cao su bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '120%.' },
      { id: 'B', text: '150%.' },
      { id: 'C', text: '200%.' },
      { id: 'D', text: '250%.' }
    ],
    answer: 'B',
    solution: `- Cà phê: Xuất khẩu = 240, Tỷ trọng XK = 80%.
$\rightarrow$ Sản lượng Cà phê = $240 / 80\% = 300$ (nghìn tấn).
- Sản lượng Cao su = 120 (đề cho).
- Mức độ cao hơn = $\frac{300 - 120}{120} \times 100\% = \frac{180}{120} \times 100\% = 150\%$.

**Chọn B.***`,
    hint1: `Tìm sản lượng Cà phê bằng cách lấy khối lượng Xuất khẩu chia cho tỷ trọng Xuất khẩu (80%).`,
    hint2: `Sản lượng Cà phê = 300. So với 120 thì cao hơn (300 - 120)/120.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'percentage-difference']
  },
  {
    id: 'dgnl_data_64',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Sản xuất và tiêu thụ Nông sản tỉnh X (2024)',
    stem: `Tỷ trọng xuất khẩu chung của cả 3 loại nông sản (Gạo, Cà phê, Cao su) trong năm 2024 là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '60,0%.' },
      { id: 'B', text: '62,5%.' },
      { id: 'C', text: '65,0%.' },
      { id: 'D', text: '70,0%.' }
    ],
    answer: 'C',
    solution: `- Tổng Xuất khẩu = $900 \text{ (Gạo)} + 240 \text{ (Cà phê)} + 108 \text{ (Cao su)} = 1248$ (nghìn tấn).
- Tổng Sản lượng = $1500 \text{ (Gạo)} + 300 \text{ (Cà phê)} + 120 \text{ (Cao su)} = 1920$ (nghìn tấn).
- Tỷ trọng XK chung = $\frac{1248}{1920} \times 100\% = 65,0\%$.

**Chọn C.***`,
    hint1: `Tính tổng khối lượng xuất khẩu của cả 3 loại rồi chia cho tổng sản lượng của cả 3 loại.`,
    hint2: `1248 / 1920 = 65%.`,
    estimatedSeconds: 35,
    tags: ['data_analysis', 'dgnl', 'proportion']
  },
  {
    id: 'dgnl_data_65',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Sản xuất và tiêu thụ Nông sản tỉnh X (2024)',
    stem: `Biết rằng giá xuất khẩu bình quân năm 2024 của Gạo là 600 USD/tấn, Cà phê là 3.000 USD/tấn và Cao su là 1.500 USD/tấn. Tổng kim ngạch xuất khẩu của 3 mặt hàng này trong năm 2024 đạt bao nhiêu triệu USD?`,
    choices: [
      { id: 'A', text: '1.285 triệu USD.' },
      { id: 'B', text: '1.320 triệu USD.' },
      { id: 'C', text: '1.422 triệu USD.' },
      { id: 'D', text: '1.516 triệu USD.' }
    ],
    answer: 'C',
    solution: `Do đơn vị khối lượng là nghìn tấn, nên: 1 nghìn tấn $\times$ 1 USD/tấn = 1 nghìn USD.
Ta tính theo nghìn USD rồi đổi ra triệu USD (chia 1000): - Gạo: $900 \times 600 = 540.000$ (nghìn USD) = 540 triệu USD.
- Cà phê: $240 \times 3000 = 720.000$ (nghìn USD) = 720 triệu USD.
- Cao su: $108 \times 1500 = 162.000$ (nghìn USD) = 162 triệu USD.
Tổng kim ngạch = $540 + 720 + 162 = 1.422$ (triệu USD).

**Chọn C.***`,
    hint1: `Nhân khối lượng xuất khẩu (nghìn tấn) với giá (USD/tấn), lưu ý quy đổi đơn vị cẩn thận để ra triệu USD.`,
    hint2: `Gạo mang về 540 triệu, Cà phê 720 triệu, Cao su 162 triệu.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'revenue']
  },
  {
    id: 'dgnl_data_66',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Sản xuất và tiêu thụ Nông sản tỉnh X (2024)',
    stem: `Giả sử trong năm 2025, do ảnh hưởng của thời tiết, Sản lượng Gạo giảm 10% so với năm 2024. Nếu Khối lượng tiêu thụ nội địa của Gạo trong năm 2025 vẫn giữ nguyên như năm 2024, thì Khối lượng xuất khẩu Gạo trong năm 2025 sẽ giảm bao nhiêu phần trăm so với năm 2024?`,
    choices: [
      { id: 'A', text: '10,00%.' },
      { id: 'B', text: '15,50%.' },
      { id: 'C', text: '16,67%.' },
      { id: 'D', text: '20,00%.' }
    ],
    answer: 'C',
    solution: `- Sản lượng Gạo 2024 = 1500.
- Sản lượng Gạo 2025 (giảm 10%) = $1500 \times 0,9 = 1350$ (nghìn tấn).
- Tiêu thụ nội địa 2025 giữ nguyên = 600.
$\rightarrow$ Xuất khẩu 2025 = $1350 - 600 = 750$ (nghìn tấn).
- Xuất khẩu 2024 là 900.
Tỷ lệ giảm khối lượng xuất khẩu = $\frac{900 - 750}{900} \times 100\% = \frac{150}{900} \times 100\% \approx 16,67\%$.

**Chọn C.***`,
    hint1: `Tính sản lượng mới của năm 2025, sau đó trừ đi mức nội địa (600) để ra phần dành cho xuất khẩu.`,
    hint2: `XK 2025 = 750. So sánh với 900 của năm 2024.`,
    estimatedSeconds: 40,
    tags: ['data_analysis', 'dgnl', 'projection']
  }
,
  {
    id: 'dgnl_data_67',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Số lượng và tỷ lệ SV tốt nghiệp có việc làm 3 khoa (2025)',
    passageText: `Bảng thống kê số lượng và tỷ lệ sinh viên tốt nghiệp có việc làm của 3 khoa trong Trường Đại học Z năm 2025. Biết rằng tỷ lệ có việc làm được tính bằng (Số SV có việc làm / Tổng số SV tốt nghiệp) ×100%.
\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Kinh tế",
      "Tổng số SV tốt nghiệp": 800,
      "Số SV có việc làm": "?(1)",
      "Tỷ lệ có việc làm (%)": 90
    },
    {
      "name": "Kỹ thuật",
      "Tổng số SV tốt nghiệp": "?(2)",
      "Số SV có việc làm": 510,
      "Tỷ lệ có việc làm (%)": 85
    },
    {
      "name": "Ngoại ngữ",
      "Tổng số SV tốt nghiệp": 400,
      "Số SV có việc làm": 380,
      "Tỷ lệ có việc làm (%)": 95
    },
    {
      "name": "Tổng toàn trường",
      "Tổng số SV tốt nghiệp": "?(3)",
      "Số SV có việc làm": "?(4)",
      "Tỷ lệ có việc làm (%)": "?(5)"
    }
  ],
  "options": {
    "bars": [
      "Tổng số SV tốt nghiệp",
      "Số SV có việc làm",
      "Tỷ lệ có việc làm (%)"
    ]
  }
}
\`\`\``,
    stem: `Tổng số sinh viên tốt nghiệp có việc làm của cả 3 khoa (giá trị của ?(4)) là bao nhiêu sinh viên?`,
    choices: [
      { id: 'A', text: '1.500.' },
      { id: 'B', text: '1.580.' },
      { id: 'C', text: '1.610.' },
      { id: 'D', text: '1.800.' }
    ],
    answer: 'C',
    solution: `- Số SV có việc Khoa Kinh tế = $800 \times 90\% = 720$.
- Số SV có việc Khoa Kỹ thuật = 510 (đề cho).
- Số SV có việc Khoa Ngoại ngữ = 380 (đề cho).
Tổng số SV có việc = $720 + 510 + 380 = 1610$ (sinh viên).

**Chọn C.***`,
    hint1: `Tìm Số SV có việc của khoa Kinh tế bằng cách nhân Tổng số SV với Tỷ lệ. Sau đó cộng cả 3 khoa lại.`,
    hint2: `720 + 510 + 380.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_68',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Số lượng và tỷ lệ SV tốt nghiệp có việc làm 3 khoa (2025)',
    stem: `Tỷ lệ có việc làm trung bình của toàn trường Đại học Z (giá trị của ?(5)) xấp xỉ bằng bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '90,0%.' },
      { id: 'B', text: '89,4%.' },
      { id: 'C', text: '91,2%.' },
      { id: 'D', text: '88,5%.' }
    ],
    answer: 'B',
    solution: `- Tổng SV có việc toàn trường = 1610 (đã tính ở câu trước).
- Tính Tổng SV tốt nghiệp của khoa Kỹ thuật = $510 / 85\% = 600$.
- Tổng SV tốt nghiệp toàn trường = $800 + 600 + 400 = 1800$.
Tỷ lệ có việc làm trung bình = $\frac{1610}{1800} \times 100\% \approx 89,444\% \approx 89,4\%$.

**Chọn B.***`,
    hint1: `Tính Tổng số SV tốt nghiệp của toàn trường (cần tìm SV khoa Kỹ thuật trước = 510 / 85%). Sau đó chia Tổng có việc cho Tổng SV.`,
    hint2: `Tổng SV = 1800. Tỷ lệ = 1610 / 1800.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'average']
  },
  {
    id: 'dgnl_data_69',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Số lượng và tỷ lệ SV tốt nghiệp có việc làm 3 khoa (2025)',
    stem: `Trường Đại học Z đặt mục tiêu trong năm 2026, tỷ lệ có việc làm của toàn trường phải đạt chính xác 92%. Giả sử số lượng SV tốt nghiệp của 3 khoa trong năm 2026 không thay đổi so với năm 2025, tỷ lệ có việc làm của Khoa Kinh tế giữ nguyên ở 90%, tỷ lệ có việc làm của Khoa Ngoại ngữ đạt mức tối đa là 100%. Hỏi tỷ lệ có việc làm của Khoa Kỹ thuật trong năm 2026 phải đạt tối thiểu bao nhiêu phần trăm để hoàn thành mục tiêu chung của trường?`,
    choices: [
      { id: 'A', text: '90%.' },
      { id: 'B', text: '92%.' },
      { id: 'C', text: '92,67%.' },
      { id: 'D', text: '94,33%.' }
    ],
    answer: 'C',
    solution: `**Lưu ý đề bài: ** Có thể có giả định Ngoại ngữ giữ nguyên ở 95% trong đáp án gốc của tác giả. Ta sẽ phân tích cả 2 trường hợp.
- Tổng SV năm 2026 = 1800.
- Số SV có việc cần đạt = $1800 \times 92\% = 1656$.
- Khoa Kinh tế giữ nguyên 90% $\rightarrow$ có $720$ SV có việc.
Nếu Ngoại ngữ đạt **tối đa 100%**: Số SV có việc = 400.
Khi đó Kỹ thuật cần: $1656 - 720 - 400 = 536$.
Tỷ lệ Kỹ thuật = $\frac{536}{600} \times 100\% \approx 89,33\%$.
Tuy nhiên, nếu Ngoại ngữ **giữ nguyên mức 95%** (giống Kinh tế giữ nguyên): Số SV có việc = 380.
Khi đó Kỹ thuật cần: $1656 - 720 - 380 = 556$.
Tỷ lệ Kỹ thuật = $\frac{556}{600} \times 100\% = 92,67\%$.
Dựa vào các đáp án A, B, C, D, đáp án phù hợp với logic tính toán là C (tương ứng với việc Ngoại ngữ giữ nguyên mức 95%).

**Chọn C.***`,
    hint1: `Tìm tổng số SV có việc cần đạt toàn trường (92% của 1800). Trừ đi số SV của Kinh tế và Ngoại ngữ (giữ nguyên tỷ lệ năm cũ) để tìm phần của Kỹ thuật.`,
    hint2: `Kỹ thuật cần 556 SV có việc. Tỷ lệ = 556 / 600.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'optimization']
  },
  {
    id: 'dgnl_data_70',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Phát thải CO2 của 3 quốc gia A, B, C (2020-2024)',
    passageText: `Biểu đồ đường thể hiện Lượng phát thải CO2 (triệu tấn) của 3 quốc gia A, B, C từ năm 2020 đến năm 2024.
- **Quốc gia A**: 2020(400); 2021(380); 2022(350); 2023(320); 2024(300)
- **Quốc gia B**: 2020(150); 2021(180); 2022(200); 2023(240); 2024(250)
- **Quốc gia C**: 2020(200); 2021(200); 2022(210); 2023(230); 2024(240)`,
    stem: `Năm nào Quốc gia A có lượng phát thải CO2 bằng với tổng lượng phát thải của Quốc gia B và Quốc gia C cộng lại?`,
    choices: [
      { id: 'A', text: '2021.' },
      { id: 'B', text: '2022.' },
      { id: 'C', text: '2023.' },
      { id: 'D', text: '2024.' }
    ],
    answer: 'A',
    solution: `Ta kiểm tra từng năm: - 2021: B + C = $180 + 200 = 380$. Lượng phát thải của A = 380. (Bằng nhau)
- 2022: B + C = $200 + 210 = 410 \neq 350$.
- 2023: B + C = $240 + 230 = 470 \neq 320$.
- 2024: B + C = $250 + 240 = 490 \neq 300$.

**Chọn A.***`,
    hint1: `Cộng giá trị của hai đường B và C ở từng năm rồi so sánh với giá trị đường A.`,
    hint2: `Năm 2021: 180 + 200 = 380.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'line-chart']
  },
  {
    id: 'dgnl_data_71',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Phát thải CO2 của 3 quốc gia A, B, C (2020-2024)',
    stem: `Trong giai đoạn 2020 - 2024, lượng phát thải CO2 của Quốc gia B đã có tốc độ tăng trưởng là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '40%.' },
      { id: 'B', text: '55,5%.' },
      { id: 'C', text: '66,67%.' },
      { id: 'D', text: '100%.' }
    ],
    answer: 'C',
    solution: `- Quốc gia B năm 2020: 150.
- Quốc gia B năm 2024: 250.
Tốc độ tăng trưởng = $\frac{250 - 150}{150} \times 100\% = \frac{100}{150} \times 100\% = 66,67\%$.

**Chọn C.***`,
    hint1: `Lấy giá trị năm cuối (2024) trừ năm đầu (2020) rồi chia cho năm đầu.`,
    hint2: `(250 - 150) / 150.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_72',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Phát thải CO2 của 3 quốc gia A, B, C (2020-2024)',
    stem: `Vào năm 2025, Quốc gia A cam kết sẽ tiếp tục giảm phát thải CO2 theo đúng tốc độ giảm phần trăm của năm 2024 so với 2023. Trong khi đó, Quốc gia B nỗ lực kiểm soát để lượng phát thải năm 2025 chỉ bằng chính xác lượng phát thải năm 2024. Nếu điều này xảy ra, tỷ lệ lượng phát thải CO2 của Quốc gia B so với Quốc gia A trong năm 2025 xấp xỉ bằng bao nhiêu?`,
    choices: [
      { id: 'A', text: '83,3%.' },
      { id: 'B', text: '86,5%.' },
      { id: 'C', text: '88,9%.' },
      { id: 'D', text: '92,1%.' }
    ],
    answer: 'C',
    solution: `- Tốc độ giảm của A năm 2024 so với 2023 = $\frac{300 - 320}{320} = -6,25\%$.
- Lượng phát thải của A năm 2025 (giảm tiếp $6,25\%$) = $300 \times (1 - 6,25\%) = 300 \times 0,9375 = 281,25$.
- Lượng phát thải của B năm 2025 = Lượng phát thải B năm 2024 = 250.
- Tỷ lệ B so với A = $\frac{250}{281,25} \times 100\% \approx 88,888\% \approx 88,9\%$.

**Chọn C.***`,
    hint1: `Tính tốc độ giảm của A trong năm 2024 so với 2023 để áp dụng cho năm 2025.`,
    hint2: `A giảm 6.25%. A(2025) = 281.25. B(2025) = 250. Lập tỉ số 250 / 281.25.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'projection']
  }
,
  {
    id: 'dgnl_data_73',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Thu nhập gia đình anh A năm 2024',
    passageText: `Biểu đồ tròn thể hiện Cơ cấu phân bổ thu nhập hàng tháng của gia đình anh A trong năm 2024: - Chi cố định: 40%
- Chi linh hoạt: 30%
- Tiết kiệm: 20%
- Đầu tư: 10%
Bảng thống kê các Nguồn thu hàng tháng của anh A (Đơn vị: Triệu VNĐ): - Lương cứng: 15
- Thưởng năng suất: 5
- Thu nhập làm thêm: 10
- Tổng cộng: 30`,
    stem: `Mỗi tháng gia đình anh A dành bao nhiêu tiền cho việc Tiết kiệm?`,
    choices: [
      { id: 'A', text: '3 triệu VNĐ.' },
      { id: 'B', text: '5 triệu VNĐ.' },
      { id: 'C', text: '6 triệu VNĐ.' },
      { id: 'D', text: '9 triệu VNĐ.' }
    ],
    answer: 'C',
    solution: `- Tổng thu nhập mỗi tháng = 30 triệu VNĐ.
- Tỷ trọng Tiết kiệm = $20\%$.
- Số tiền Tiết kiệm = $30 \times 20\% = 6$ (triệu VNĐ).

**Chọn C.***`,
    hint1: `Sử dụng tổng thu nhập từ Bảng nhân với phần trăm Tiết kiệm trên Biểu đồ tròn.`,
    hint2: `30 * 20% = 6.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'combined-chart']
  },
  {
    id: 'dgnl_data_74',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Thu nhập gia đình anh A năm 2024',
    stem: `Giả sử vào tháng 1/2025, anh A bị cắt khoản "Thu nhập làm thêm", khiến tổng thu nhập giảm đi tương ứng, nhưng anh A quyết định giữ nguyên số tiền tuyệt đối dành cho "Đầu tư" như của năm 2024. Hỏi tỷ trọng của khoản "Đầu tư" trong tổng thu nhập tháng 1/2025 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '10%.' },
      { id: 'B', text: '12%.' },
      { id: 'C', text: '15%.' },
      { id: 'D', text: '20%.' }
    ],
    answer: 'C',
    solution: `- Thu nhập làm thêm = 10 triệu. Nếu bị cắt, Tổng thu nhập mới = $30 - 10 = 20$ (triệu VNĐ).
- Tiền Đầu tư năm 2024 = $30 \times 10\% = 3$ (triệu VNĐ). Anh A giữ nguyên 3 triệu này cho tháng 1/2025.
- Tỷ trọng "Đầu tư" mới = $\frac{3}{20} \times 100\% = 15\%$.

**Chọn C.***`,
    hint1: `Tính tổng thu nhập mới (sau khi trừ 10 triệu). Tính số tiền đầu tư tuyệt đối (10% của 30 triệu). Rồi lập tỉ số mới.`,
    hint2: `Tổng mới = 20. Đầu tư = 3. Tỷ lệ = 3 / 20 = 15%.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'proportion']
  },
  {
    id: 'dgnl_data_75',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Thu nhập gia đình anh A năm 2024',
    stem: `Nếu từ năm 2024 sang năm 2025, tổng thu nhập của anh A tăng đều đặn 10% mỗi tháng so với mức 30 triệu VNĐ/tháng cũ, và cơ cấu tỷ lệ phân bổ chi tiêu không thay đổi. Tổng số tiền gia đình anh A chi ra để Chi tiêu (bao gồm Cố định và Linh hoạt) trong cả 12 tháng của năm 2025 là bao nhiêu?`,
    choices: [
      { id: 'A', text: '252 triệu VNĐ.' },
      { id: 'B', text: '277,2 triệu VNĐ.' },
      { id: 'C', text: '290,4 triệu VNĐ.' },
      { id: 'D', text: '330 triệu VNĐ.' }
    ],
    answer: 'B',
    solution: `- Tổng thu nhập mỗi tháng năm 2025 = $30 \times (1 + 10\%) = 33$ (triệu VNĐ).
- Cơ cấu không đổi, nên tổng tỷ trọng Chi tiêu = Chi cố định ($40\%$) + Chi linh hoạt ($30\%$) = $70\%$.
- Tiền chi tiêu mỗi tháng năm 2025 = $33 \times 70\% = 23,1$ (triệu VNĐ).
- Tổng chi tiêu cả năm (12 tháng) = $23,1 \times 12 = 277,2$ (triệu VNĐ).

**Chọn B.***`,
    hint1: `Tính mức thu nhập hàng tháng mới (tăng 10%). Tính tỷ lệ dành cho Chi tiêu (40% + 30% = 70%). Rồi nhân cho 12 tháng.`,
    hint2: `Thu nhập = 33. Chi tiêu = 33 * 0.7 = 23.1. Nhân với 12 = 277.2.`,
    estimatedSeconds: 35,
    tags: ['data_analysis', 'dgnl', 'projection']
  },
  {
    id: 'dgnl_data_76',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Giao dịch Cổ phiếu X trong 5 phiên',
    passageText: `Biểu đồ cột (màu đỏ) thể hiện Giá đóng cửa (Nghìn đồng/cổ phiếu).
Biểu đồ cột (màu xanh) thể hiện Khối lượng giao dịch (Triệu cổ phiếu).
Biết: Giá trị giao dịch = Giá đóng cửa × Khối lượng giao dịch.
Dữ liệu 5 phiên liên tiếp (Khối lượng - Giá): - Phiên 1: 10 - 50
- Phiên 2: 12 - 52
- Phiên 3: 15 - 48
- Phiên 4: 8 - 49
- Phiên 5: 20 - 55`,
    stem: `Tổng Giá trị giao dịch của cổ phiếu X trong Phiên 3 là bao nhiêu Tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '72 tỷ VNĐ.' },
      { id: 'B', text: '150 tỷ VNĐ.' },
      { id: 'C', text: '720 tỷ VNĐ.' },
      { id: 'D', text: '7.200 tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `- Phiên 3: Khối lượng giao dịch = 15 triệu CP.
- Giá đóng cửa = 48 nghìn đồng/CP.
- Giá trị giao dịch = $15.000.000 \times 48.000 = 720.000.000.000$ VNĐ = $720$ (Tỷ VNĐ).

*Mẹo tính nhanh: * (Triệu CP) $\times$ (Nghìn đồng) = Tỷ đồng. Nên $15 \times 48 = 720$ tỷ.

**Chọn C.***`,
    hint1: `Lấy Khối lượng nhân với Giá của Phiên 3. Lưu ý đơn vị: Triệu nhân với Nghìn thì ra Tỷ.`,
    hint2: `15 * 48 = 720.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'chart-reading']
  },
  {
    id: 'dgnl_data_77',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Giao dịch Cổ phiếu X trong 5 phiên',
    stem: `Phiên giao dịch nào ghi nhận mức tăng trưởng Giá đóng cửa (tính bằng số tuyệt đối) cao nhất so với phiên giao dịch liền trước đó?`,
    choices: [
      { id: 'A', text: 'Phiên 2.' },
      { id: 'B', text: 'Phiên 3.' },
      { id: 'C', text: 'Phiên 4.' },
      { id: 'D', text: 'Phiên 5.' }
    ],
    answer: 'D',
    solution: `- Phiên 2 so với Phiên 1 = $52 - 50 = +2$.
- Phiên 3 so với Phiên 2 = $48 - 52 = -4$.
- Phiên 4 so với Phiên 3 = $49 - 48 = +1$.
- Phiên 5 so với Phiên 4 = $55 - 49 = +6$.
Mức tăng trưởng tuyệt đối cao nhất là $+6$ nghìn đồng, ghi nhận tại Phiên 5.

**Chọn D.***`,
    hint1: `Lấy giá đóng cửa của mỗi phiên trừ đi giá đóng cửa của phiên ngay trước nó, xem số nào dương lớn nhất.`,
    hint2: `Phiên 5 trừ Phiên 4: 55 - 49 = 6.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'chart-reading']
  },
  {
    id: 'dgnl_data_78',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Giao dịch Cổ phiếu X trong 5 phiên',
    stem: `Nhà đầu tư K quyết định mua 100.000 cổ phiếu X tại mức giá đóng cửa của Phiên 1, và bán toàn bộ số cổ phiếu này tại mức giá đóng cửa của Phiên 5. Biết rằng công ty chứng khoán thu phí giao dịch là 0,2% tính trên mỗi chiều (chiều mua và chiều bán). Lợi nhuận ròng (sau khi trừ phí) của nhà đầu tư K trong thương vụ này là bao nhiêu triệu VNĐ?`,
    choices: [
      { id: 'A', text: '450,0 triệu VNĐ.' },
      { id: 'B', text: '479,0 triệu VNĐ.' },
      { id: 'C', text: '489,5 triệu VNĐ.' },
      { id: 'D', text: '500,0 triệu VNĐ.' }
    ],
    answer: 'B',
    solution: `- Chiều MUA (Phiên 1): Giá 50 nghìn đồng/CP.
Giá trị mua = $100.000 \times 50.000 = 5.000.000.000$ VNĐ = $5.000$ (triệu VNĐ).
Phí mua = $5.000 \times 0,2\% = 10$ (triệu VNĐ).
Tổng chi phí vốn = $5.000 + 10 = 5.010$ (triệu VNĐ).

- Chiều BÁN (Phiên 5): Giá 55 nghìn đồng/CP.
Giá trị bán = $100.000 \times 55.000 = 5.500.000.000$ VNĐ = $5.500$ (triệu VNĐ).
Phí bán = $5.500 \times 0,2\% = 11$ (triệu VNĐ).
Tiền thu về thực tế = $5.500 - 11 = 5.489$ (triệu VNĐ).

- Lợi nhuận ròng = Tiền thu về thực tế - Tổng chi phí vốn = $5.489 - 5.010 = 479$ (triệu VNĐ).

**Chọn B.***`,
    hint1: `Tính giá trị lúc mua và phí mua (chi phí vốn). Tính giá trị lúc bán và phí bán (tiền thu về thực tế). Lấy Thu trừ Chi.`,
    hint2: `Giá trị mua 5 tỷ (phí 10 tr). Giá trị bán 5.5 tỷ (phí 11 tr). 5500 - 11 - (5000 + 10) = 479.`,
    estimatedSeconds: 60,
    tags: ['data_analysis', 'dgnl', 'finance']
  },
  {
    id: 'dgnl_data_79',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Khảo sát thị hiếu 3 sản phẩm điện thoại (A, B, C)',
    passageText: `Một công ty thực hiện khảo sát thị hiếu của 1.000 khách hàng đối với 3 sản phẩm điện thoại A, B, C theo 3 nhóm độ tuổi. Bảng dữ liệu: \`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Dưới 18 tuổi",
      "SP A": 120,
      "SP B": 50,
      "SP C": "?(1)",
      "Tổng cộng": 200
    },
    {
      "name": "18 - 30 tuổi",
      "SP A": "?(2)",
      "SP B": 150,
      "SP C": 80,
      "Tổng cộng": 400
    },
    {
      "name": "Trên 30 tuổi",
      "SP A": 60,
      "SP B": "?(3)",
      "SP C": 100,
      "Tổng cộng": "?(4)"
    },
    {
      "name": "Tổng cộng",
      "SP A": 350,
      "SP B": "?",
      "SP C": 210,
      "Tổng cộng": 1000
    }
  ],
  "options": {
    "bars": [
      "SP A",
      "SP B",
      "SP C",
      "Tổng cộng"
    ]
  }
}
\`\`\``,
    stem: `Số lượng khách hàng Trên 30 tuổi mua Sản phẩm B (giá trị ?(3)) là bao nhiêu người?`,
    choices: [
      { id: 'A', text: '200 người.' },
      { id: 'B', text: '220 người.' },
      { id: 'C', text: '240 người.' },
      { id: 'D', text: '260 người.' }
    ],
    answer: 'C',
    solution: `- Tổng số người Dưới 18 = 200; Từ 18-30 = 400.
$\rightarrow$ Tổng nhóm Trên 30 = $1000 - 200 - 400 = 400$ người (giá trị ?(4)).
- Nhóm Trên 30 tuổi mua SP A = 60, SP C = 100.
$\rightarrow$ Nhóm Trên 30 tuổi mua SP B = $400 - 60 - 100 = 240$ (người).

**Chọn C.***`,
    hint1: `Đầu tiên tìm tổng số người của nhóm Trên 30 tuổi (bằng 1000 trừ đi hai nhóm kia). Sau đó trừ tiếp số người mua A và C trong nhóm này.`,
    hint2: `Tổng Trên 30 = 400. Mua B = 400 - 60 - 100 = 240.`,
    estimatedSeconds: 25,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_80',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Khảo sát thị hiếu 3 sản phẩm điện thoại (A, B, C)',
    stem: `Trong nhóm khách hàng từ 18 đến 30 tuổi, sản phẩm nào được ưa chuộng (mua nhiều) nhất?`,
    choices: [
      { id: 'A', text: 'Sản phẩm A.' },
      { id: 'B', text: 'Sản phẩm B.' },
      { id: 'C', text: 'Sản phẩm C.' },
      { id: 'D', text: 'A và B bằng nhau.' }
    ],
    answer: 'A',
    solution: `- Số lượng người mua B = 150.
- Số lượng người mua C = 80.
- Tổng số người nhóm này = 400.
$\rightarrow$ Số lượng người mua A = $400 - 150 - 80 = 170$ (người).
Vì $170 > 150 > 80$, nên SP A được ưa chuộng nhất.

**Chọn A.***`,
    hint1: `Tìm số người mua sản phẩm A ở nhóm 18-30 tuổi bằng cách lấy 400 trừ đi B và C.`,
    hint2: `A = 170. B = 150. C = 80.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_81',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Khảo sát thị hiếu 3 sản phẩm điện thoại (A, B, C)',
    stem: `Tỷ lệ khách hàng mua Sản phẩm B trong tổng số người tham gia khảo sát là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '35%.' },
      { id: 'B', text: '40%.' },
      { id: 'C', text: '44%.' },
      { id: 'D', text: '50%.' }
    ],
    answer: 'C',
    solution: `- Tổng khách mua SP A = 350. Tổng khách mua SP C = 210.
- Tổng người khảo sát = 1000.
- Tổng khách mua SP B = $1000 - 350 - 210 = 440$.
- Tỷ lệ = $\frac{440}{1000} \times 100\% = 44\%$.

**Chọn C.***`,
    hint1: `Tìm tổng số khách hàng mua SP B (cột dọc) rồi chia cho 1000.`,
    hint2: `Tổng B = 1000 - 350 - 210 = 440.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'proportion']
  },
  {
    id: 'dgnl_data_82',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Khảo sát thị hiếu 3 sản phẩm điện thoại (A, B, C)',
    stem: `Biết rằng mức lợi nhuận thu được từ mỗi chiếc điện thoại loại A là 2 triệu đồng, loại B là 1,5 triệu đồng và loại C là 3 triệu đồng. Tổng lợi nhuận công ty thu được từ riêng nhóm khách hàng Trên 30 tuổi là bao nhiêu triệu đồng?`,
    choices: [
      { id: 'A', text: '740 triệu đồng.' },
      { id: 'B', text: '760 triệu đồng.' },
      { id: 'C', text: '780 triệu đồng.' },
      { id: 'D', text: '800 triệu đồng.' }
    ],
    answer: 'C',
    solution: `- Nhóm Trên 30 tuổi mua: + SP A: 60 cái $\rightarrow$ Lợi nhuận = $60 \times 2 = 120$ (triệu).
  + SP B: 240 cái (đã tính ở Câu 1) $\rightarrow$ Lợi nhuận = $240 \times 1,5 = 360$ (triệu).
  + SP C: 100 cái $\rightarrow$ Lợi nhuận = $100 \times 3 = 300$ (triệu).
- Tổng lợi nhuận từ nhóm Trên 30 tuổi = $120 + 360 + 300 = 780$ (triệu đồng).

**Chọn C.***`,
    hint1: `Lấy số lượng của từng sản phẩm ở hàng "Trên 30 tuổi" nhân với mức lợi nhuận tương ứng rồi cộng lại.`,
    hint2: `60*2 + 240*1.5 + 100*3 = 780.`,
    estimatedSeconds: 25,
    tags: ['data_analysis', 'dgnl', 'revenue']
  },
  {
    id: 'dgnl_data_83',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Khảo sát thị hiếu 3 sản phẩm điện thoại (A, B, C)',
    stem: `Giả sử trong đợt bán hàng tới, công ty tung ra chiến dịch khuyến mãi dành riêng cho Sản phẩm C. Theo dự báo, số lượng người mua Sản phẩm C ở nhóm "18 - 30 tuổi" sẽ tăng 25% và ở nhóm "Trên 30 tuổi" tăng 15% so với khảo sát hiện tại; các thông số khác hoàn toàn giữ nguyên. Khi đó, tỷ trọng khách hàng mua Sản phẩm C so với tổng số khách hàng (đã tăng) xấp xỉ bằng bao nhiêu?`,
    choices: [
      { id: 'A', text: '22,5%.' },
      { id: 'B', text: '23,7%.' },
      { id: 'C', text: '24,1%.' },
      { id: 'D', text: '24,5%.' }
    ],
    answer: 'B',
    solution: `Tính số khách mua SP C tăng thêm: - Nhóm "18 - 30 tuổi": tăng $25\%$ của 80 người = $80 \times 0,25 = 20$ người.
- Nhóm "Trên 30 tuổi": tăng $15\%$ của 100 người = $100 \times 0,15 = 15$ người.
- Tổng số lượng khách hàng mới mua SP C = Tổng SP C cũ + Số tăng thêm = $210 + 20 + 15 = 245$ (người).
- Vì các thông số khác giữ nguyên nên Tổng số khách hàng cũng tăng đúng bằng lượng mua thêm của SP C.
- Tổng khách hàng mới = $1000 + 20 + 15 = 1035$ (người).
- Tỷ trọng SP C mới = $\frac{245}{1035} \times 100\% \approx 23,67\% \approx 23,7\%$.

**Chọn B.***`,
    hint1: `Tìm số người tăng thêm ở SP C (20 + 15 = 35). Cộng số này vào tổng SP C cũ và cũng cộng vào Tổng số 1000 khách hàng ban đầu.`,
    hint2: `SP C mới = 245. Tổng KH mới = 1035. Tỷ lệ = 245 / 1035 = 23.67%.`,
    estimatedSeconds: 50,
    tags: ['data_analysis', 'dgnl', 'proportion']
  }
,
  {
    id: 'dgnl_data_84',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Tăng trưởng GDP và Lạm phát 4 quốc gia (2023)',
    passageText: `Biểu đồ cột ghép dưới đây thể hiện Tỷ lệ Tăng trưởng GDP (%) và Tỷ lệ Lạm phát (%) của 4 quốc gia Đông Nam Á trong năm 2023.
Biết: Chênh lệch thực tế = Tăng trưởng GDP - Lạm phát.
Dữ liệu 4 quốc gia (Tăng trưởng ; Lạm phát): - Việt Nam: 5% ; 3%
- Thái Lan: 2,5% ; 1,5%
- Malaysia: 4% ; 2,5%
- Indonesia: 5% ; 4%`,
    stem: `Quốc gia nào có mức chênh lệch thực tế giữa Tăng trưởng GDP và Lạm phát lớn nhất trong năm 2023?`,
    choices: [
      { id: 'A', text: 'Việt Nam.' },
      { id: 'B', text: 'Thái Lan.' },
      { id: 'C', text: 'Malaysia.' },
      { id: 'D', text: 'Indonesia.' }
    ],
    answer: 'A',
    solution: `Mức chênh lệch thực tế của các quốc gia: - Việt Nam = $5 - 3 = 2\%$.
- Thái Lan = $2,5 - 1,5 = 1\%$.
- Malaysia = $4 - 2,5 = 1,5\%$.
- Indonesia = $5 - 4 = 1\%$.
Việt Nam có mức chênh lệch thực tế lớn nhất ($2\%$).

**Chọn A.***`,
    hint1: `Dùng phép trừ (Tăng trưởng trừ đi Lạm phát) cho từng quốc gia.`,
    hint2: `Việt Nam: 5 - 3 = 2 (cao nhất).`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'chart-reading']
  },
  {
    id: 'dgnl_data_85',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Tăng trưởng GDP và Lạm phát 4 quốc gia (2023)',
    stem: `Trung bình cộng tỷ lệ Tăng trưởng GDP của 4 quốc gia được khảo sát là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '3,50%.' },
      { id: 'B', text: '3,85%.' },
      { id: 'C', text: '4,125%.' },
      { id: 'D', text: '4,25%.' }
    ],
    answer: 'C',
    solution: `Tổng tỷ lệ tăng trưởng GDP của 4 quốc gia: $5 + 2,5 + 4 + 5 = 16,5\%$.
Trung bình cộng = $\frac{16,5}{4} = 4,125\%$.

**Chọn C.***`,
    hint1: `Lấy số liệu Tăng trưởng GDP của 4 nước cộng lại rồi chia cho 4.`,
    hint2: `(5 + 2.5 + 4 + 5) / 4 = 4.125.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'average']
  },
  {
    id: 'dgnl_data_86',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Tăng trưởng GDP và Lạm phát 4 quốc gia (2023)',
    stem: `Nếu quy mô nền kinh tế (GDP danh nghĩa) của Malaysia vào cuối năm 2022 là 400 tỷ USD, với mức tăng trưởng GDP năm 2023 như trên biểu đồ, thì quy mô kinh tế của Malaysia đã tăng thêm bao nhiêu tỷ USD trong năm 2023?`,
    choices: [
      { id: 'A', text: '10 tỷ USD.' },
      { id: 'B', text: '12 tỷ USD.' },
      { id: 'C', text: '16 tỷ USD.' },
      { id: 'D', text: '20 tỷ USD.' }
    ],
    answer: 'C',
    solution: `Tăng trưởng GDP của Malaysia năm 2023 là $4\%$.
Quy mô tăng thêm = $400 \times 4\% = 16$ (tỷ USD).

**Chọn C.***`,
    hint1: `Nhân quy mô năm 2022 với phần trăm tăng trưởng GDP của Malaysia.`,
    hint2: `400 * 4% = 16.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_87',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Tăng trưởng GDP và Lạm phát 4 quốc gia (2023)',
    stem: `Giả sử quy mô nền kinh tế của Việt Nam vào cuối năm 2022 là 400 tỷ USD. Nếu mức Chênh lệch thực tế (Tăng trưởng GDP - Lạm phát) của năm 2023 được xem là "Tốc độ tăng trưởng thực" và Việt Nam duy trì tốc độ tăng trưởng thực này không đổi trong suốt 3 năm liên tiếp (2023, 2024, 2025), thì đến cuối năm 2025, quy mô kinh tế thực của Việt Nam xấp xỉ bằng bao nhiêu tỷ USD?`,
    choices: [
      { id: 'A', text: '415,20 tỷ USD.' },
      { id: 'B', text: '424,48 tỷ USD.' },
      { id: 'C', text: '440,00 tỷ USD.' },
      { id: 'D', text: '463,05 tỷ USD.' }
    ],
    answer: 'B',
    solution: `- Tốc độ tăng trưởng thực của Việt Nam = $5\% - 3\% = 2\%$.
- Dựa theo công thức lãi kép, quy mô kinh tế sau 3 năm: $V = 400 \times (1 + 2\%)^3 = 400 \times 1,02^3$
$V = 400 \times 1,061208 = 424,4832$ (tỷ USD).

**Chọn B.***`,
    hint1: `Lấy mức chênh lệch (2%) làm mức tăng trưởng. Tính theo lãi kép sau 3 năm: Gốc * (1 + 2%)^3.`,
    hint2: `400 * 1.02 * 1.02 * 1.02 = 424.48.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'compound-interest']
  },
  {
    id: 'dgnl_data_88',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Hoạt động kinh doanh Công ty T (4 Quý năm 2024)',
    passageText: `Bảng dữ liệu về Doanh thu và Chi phí của Công ty T trong 4 quý.
Biết: Mức độ hoàn thành = (Doanh thu thực tế / Mục tiêu DT) × 100%.
Lợi nhuận = Doanh thu thực tế - Chi phí thực tế.
\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Quý 1",
      "Doanh thu TT (Tỷ)": 120,
      "Chi phí TT (Tỷ)": 80,
      "Mục tiêu DT (Tỷ)": 100,
      "Hoàn thành mục tiêu": 120
    },
    {
      "name": "Quý 2",
      "Doanh thu TT (Tỷ)": 150,
      "Chi phí TT (Tỷ)": 90,
      "Mục tiêu DT (Tỷ)": 120,
      "Hoàn thành mục tiêu": 125
    },
    {
      "name": "Quý 3",
      "Doanh thu TT (Tỷ)": "?(1)",
      "Chi phí TT (Tỷ)": 100,
      "Mục tiêu DT (Tỷ)": 150,
      "Hoàn thành mục tiêu": 80
    },
    {
      "name": "Quý 4",
      "Doanh thu TT (Tỷ)": 210,
      "Chi phí TT (Tỷ)": "?(2)",
      "Mục tiêu DT (Tỷ)": 200,
      "Hoàn thành mục tiêu": 105
    }
  ],
  "options": {
    "bars": [
      "Doanh thu TT (Tỷ)",
      "Chi phí TT (Tỷ)",
      "Mục tiêu DT (Tỷ)",
      "Hoàn thành mục tiêu"
    ]
  }
}
\`\`\``,
    stem: `Doanh thu thực tế của Công ty T trong Quý 3 (giá trị ?(1)) là bao nhiêu tỷ đồng?`,
    choices: [
      { id: 'A', text: '110 tỷ.' },
      { id: 'B', text: '120 tỷ.' },
      { id: 'C', text: '130 tỷ.' },
      { id: 'D', text: '140 tỷ.' }
    ],
    answer: 'B',
    solution: `- Từ công thức: Mức độ hoàn thành = (Doanh thu thực tế / Mục tiêu DT) × 100%.
- Tại Quý 3: $80\% = \frac{?(1)}{150} \times 100\%$.
$\rightarrow ?(1) = 150 \times 80\% = 120$ (tỷ đồng).

**Chọn B.***`,
    hint1: `Sử dụng công thức ở đề bài: Doanh thu thực tế = Mục tiêu DT * Hoàn thành mục tiêu.`,
    hint2: `150 * 80% = 120.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_89',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Hoạt động kinh doanh Công ty T (4 Quý năm 2024)',
    stem: `Tổng Doanh thu thực tế của Cả năm 2024 đã vượt Tổng Mục tiêu Doanh thu của cả năm là bao nhiêu phần trăm? (Làm tròn đến 2 chữ số thập phân).`,
    choices: [
      { id: 'A', text: '4,50%.' },
      { id: 'B', text: '5,00%.' },
      { id: 'C', text: '5,26%.' },
      { id: 'D', text: '6,15%.' }
    ],
    answer: 'C',
    solution: `- Tổng Doanh thu thực tế 4 quý = $120 + 150 + 120 \text{ (Quý 3)} + 210 = 600$ (tỷ đồng).
- Tổng Mục tiêu Doanh thu 4 quý = $100 + 120 + 150 + 200 = 570$ (tỷ đồng).
- Phần trăm vượt = $\frac{600 - 570}{570} \times 100\% = \frac{30}{570} \times 100\% \approx 5,263\% \approx 5,26\%$.

**Chọn C.***`,
    hint1: `Tính tổng cột Doanh thu thực tế và tổng cột Mục tiêu DT. Lấy độ chênh lệch chia cho tổng Mục tiêu DT.`,
    hint2: `Tổng TT = 600. Tổng Mục tiêu = 570. Vượt: 30 / 570.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_90',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Hoạt động kinh doanh Công ty T (4 Quý năm 2024)',
    stem: `Biết rằng Biên lợi nhuận (Tỷ lệ Lợi nhuận trên Doanh thu thực tế) của Quý 4 đạt mức 30%. Hỏi Lợi nhuận thực tế của Quý 4 cao gấp bao nhiêu lần Lợi nhuận thực tế của Quý 1?`,
    choices: [
      { id: 'A', text: '1,25 lần.' },
      { id: 'B', text: '1,40 lần.' },
      { id: 'C', text: '1,575 lần.' },
      { id: 'D', text: '1,75 lần.' }
    ],
    answer: 'C',
    solution: `- Lợi nhuận Quý 4 = Biên LN $\times$ Doanh thu thực tế Q4 = $30\% \times 210 = 63$ (tỷ đồng).
- Lợi nhuận Quý 1 = Doanh thu thực tế Q1 - Chi phí thực tế Q1 = $120 - 80 = 40$ (tỷ đồng).
- Số lần gấp = $\frac{63}{40} = 1,575$ (lần).

**Chọn C.***`,
    hint1: `Tính lợi nhuận Quý 4 (bằng 30% của 210). Tính lợi nhuận Quý 1 (lấy Doanh thu trừ Chi phí).`,
    hint2: `Lợi nhuận Q4 = 63. Lợi nhuận Q1 = 40. 63 / 40 = 1.575.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'profit']
  },
  {
    id: 'dgnl_data_91',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Hoạt động kinh doanh Công ty T (4 Quý năm 2024)',
    stem: `Giả sử Chi phí thực tế của Quý 4 bằng đúng trung bình cộng Chi phí thực tế của 3 quý đầu năm. Khi đó, Tổng chi phí thực tế của cả năm 2024 chiếm bao nhiêu phần trăm trong Tổng doanh thu thực tế của Công ty T?`,
    choices: [
      { id: 'A', text: '55,5%.' },
      { id: 'B', text: '60,0%.' },
      { id: 'C', text: '62,5%.' },
      { id: 'D', text: '65,0%.' }
    ],
    answer: 'B',
    solution: `- Chi phí thực tế 3 quý đầu: Q1 = 80, Q2 = 90, Q3 = 100.
$\rightarrow$ Trung bình cộng = $\frac{80 + 90 + 100}{3} = 90$.
Vậy Chi phí Quý 4 = 90 (tỷ đồng).
- Tổng chi phí thực tế cả năm = $80 + 90 + 100 + 90 = 360$ (tỷ đồng).
- Tổng doanh thu thực tế cả năm (đã tính ở câu trước) = 600 (tỷ đồng).
- Tỷ trọng = $\frac{360}{600} \times 100\% = 60,0\%$.

**Chọn B.***`,
    hint1: `Tìm Chi phí Quý 4 bằng trung bình cộng của 80, 90, 100. Rồi tính tỷ lệ Tổng chi phí trên Tổng doanh thu.`,
    hint2: `Tổng chi phí = 360. Tổng doanh thu = 600. 360 / 600 = 60%.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'proportion']
  }
,
  {
    id: 'dgnl_data_92',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Hoạt động vận tải Hãng H (2020-2023)',
    passageText: `Biểu đồ kết hợp cung cấp thông tin về Khối lượng hàng hóa vận chuyển (Biểu đồ cột - Nghìn tấn) và Chi phí vận tải trung bình cho 1 tấn hàng (Biểu đồ đường - Triệu VNĐ/tấn) của Hãng vận tải H từ 2020 đến 2023.
- Năm 2020: Khối lượng 50 ; Chi phí 2,0
- Năm 2021: Khối lượng 40 ; Chi phí 2,5
- Năm 2022: Khối lượng 70 ; Chi phí 1,8
- Năm 2023: Khối lượng 100 ; Chi phí 1,5`,
    stem: `Tổng chi phí vận tải của toàn bộ khối lượng hàng hóa trong năm 2022 là bao nhiêu Tỷ VNĐ?`,
    choices: [
      { id: 'A', text: '100 tỷ VNĐ.' },
      { id: 'B', text: '115 tỷ VNĐ.' },
      { id: 'C', text: '126 tỷ VNĐ.' },
      { id: 'D', text: '150 tỷ VNĐ.' }
    ],
    answer: 'C',
    solution: `- Khối lượng hàng hóa năm 2022 = 70 nghìn tấn.
- Chi phí vận tải cho 1 tấn = 1,8 triệu VNĐ.
- Tổng chi phí = $70.000 \times 1.800.000 = 126.000.000.000$ VNĐ = $126$ (tỷ VNĐ).

*Mẹo: * (Nghìn tấn) $\times$ (Triệu VNĐ) = Tỷ VNĐ. Vậy $70 \times 1,8 = 126$.

**Chọn C.***`,
    hint1: `Lấy Khối lượng năm 2022 nhân với Chi phí vận tải trung bình của năm 2022.`,
    hint2: `70 * 1.8 = 126.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'combined-chart']
  },
  {
    id: 'dgnl_data_93',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Hoạt động vận tải Hãng H (2020-2023)',
    stem: `Từ năm 2021 đến năm 2023, khối lượng hàng hóa vận chuyển đã tăng trưởng bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '100%.' },
      { id: 'B', text: '120%.' },
      { id: 'C', text: '150%.' },
      { id: 'D', text: '200%.' }
    ],
    answer: 'C',
    solution: `- Khối lượng năm 2021 = 40 (Nghìn tấn).
- Khối lượng năm 2023 = 100 (Nghìn tấn).
- Tốc độ tăng trưởng = $\frac{100 - 40}{40} \times 100\% = \frac{60}{40} \times 100\% = 150\%$.

**Chọn C.***`,
    hint1: `Dùng công thức Tốc độ tăng trưởng = (Năm sau - Năm trước) / Năm trước.`,
    hint2: `(100 - 40) / 40.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'growth']
  },
  {
    id: 'dgnl_data_94',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Hoạt động vận tải Hãng H (2020-2023)',
    stem: `Chi phí vận tải trung bình 1 tấn hàng hóa trong năm 2023 đã giảm bao nhiêu phần trăm so với mức Đỉnh (cao nhất) trong giai đoạn khảo sát?`,
    choices: [
      { id: 'A', text: '30%.' },
      { id: 'B', text: '35%.' },
      { id: 'C', text: '40%.' },
      { id: 'D', text: '50%.' }
    ],
    answer: 'C',
    solution: `- Mức Đỉnh của Chi phí vận tải là năm 2021: 2,5 triệu VNĐ/tấn.
- Mức chi phí năm 2023: 1,5 triệu VNĐ/tấn.
- Tỷ lệ giảm = $\frac{2,5 - 1,5}{2,5} \times 100\% = \frac{1,0}{2,5} \times 100\% = 40\%$.

**Chọn C.***`,
    hint1: `Tìm mức đỉnh trên biểu đồ đường (là 2.5). So sánh mức năm 2023 (là 1.5) với mức đỉnh này.`,
    hint2: `(2.5 - 1.5) / 2.5 = 40%.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'percentage-decrease']
  },
  {
    id: 'dgnl_data_95',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Hoạt động vận tải Hãng H (2020-2023)',
    stem: `So sánh Tổng chi phí vận tải của toàn bộ hàng hóa trong năm 2020 và năm 2023. Năm nào có tổng chi phí cao hơn và cao hơn bao nhiêu tỷ VNĐ?`,
    choices: [
      { id: 'A', text: 'Năm 2020 cao hơn 50 tỷ.' },
      { id: 'B', text: 'Năm 2023 cao hơn 20 tỷ.' },
      { id: 'C', text: 'Năm 2020 cao hơn 20 tỷ.' },
      { id: 'D', text: 'Năm 2023 cao hơn 50 tỷ.' }
    ],
    answer: 'D',
    solution: `- Tổng chi phí năm 2020 = $50 \times 2,0 = 100$ (tỷ VNĐ).
- Tổng chi phí năm 2023 = $100 \times 1,5 = 150$ (tỷ VNĐ).
- Năm 2023 cao hơn: $150 - 100 = 50$ (tỷ VNĐ).

**Chọn D.***`,
    hint1: `Tính tổng chi phí của từng năm (Khối lượng * Chi phí 1 tấn) rồi lấy số lớn trừ số bé.`,
    hint2: `2020: 100 tỷ. 2023: 150 tỷ. Chênh lệch = 50.`,
    estimatedSeconds: 20,
    tags: ['data_analysis', 'dgnl', 'comparison']
  },
  {
    id: 'dgnl_data_96',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Hoạt động vận tải Hãng H (2020-2023)',
    stem: `Giả sử trong năm 2024, Hãng vận tải H đặt mục tiêu giảm 20% chi phí vận tải trung bình cho 1 tấn hàng so với năm 2023. Nếu hãng mong muốn Tổng chi phí vận tải của toàn bộ hàng hóa trong năm 2024 bằng đúng Tổng chi phí của năm 2023, thì khối lượng hàng hóa hãng cần vận chuyển trong năm 2024 là bao nhiêu nghìn tấn?`,
    choices: [
      { id: 'A', text: '115 nghìn tấn.' },
      { id: 'B', text: '120 nghìn tấn.' },
      { id: 'C', text: '125 nghìn tấn.' },
      { id: 'D', text: '130 nghìn tấn.' }
    ],
    answer: 'C',
    solution: `- Chi phí 1 tấn năm 2023 = 1,5 (triệu VNĐ).
- Chi phí 1 tấn năm 2024 (giảm 20%) = $1,5 \times (1 - 20\%) = 1,2$ (triệu VNĐ).
- Tổng chi phí năm 2023 = 150 (tỷ VNĐ) (đã tính ở câu trước).
- Tổng chi phí năm 2024 = 150 (tỷ VNĐ).
$\rightarrow$ Khối lượng cần vận chuyển = $\frac{\text{Tổng chi phí}}{\text{Chi phí 1 tấn}} = \frac{150}{1,2} = 125$ (nghìn tấn).

**Chọn C.***`,
    hint1: `Tìm mức chi phí 1 tấn mới cho năm 2024 (giảm 20% so với 1.5). Rồi dùng Tổng chi phí (150 tỷ) chia cho mức chi phí mới này.`,
    hint2: `Chi phí mới = 1.2. Khối lượng = 150 / 1.2 = 125.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'optimization']
  },
  {
    id: 'dgnl_data_97',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Tuyển sinh Đại học X (2024)',
    passageText: `Bảng thống kê số lượng thí sinh đăng ký dự thi, tỷ lệ trúng tuyển và điểm chuẩn của 3 khối thi phổ biến tại Đại học X năm 2024. Biết: Tỷ lệ trúng tuyển = (Số lượng trúng tuyển / Số lượng dự thi) × 100%.
\`\`\`chart
{
  "type": "bar",
  "data": [
    {
      "name": "Khối A00",
      "SL dự thi (Ngàn người)": 300,
      "Tỷ lệ trúng tuyển (%)": 25,
      "Điểm chuẩn (Điểm)": 24.5
    },
    {
      "name": "Khối A01",
      "SL dự thi (Ngàn người)": "?(1)",
      "Tỷ lệ trúng tuyển (%)": 30,
      "Điểm chuẩn (Điểm)": 23
    },
    {
      "name": "Khối D01",
      "SL dự thi (Ngàn người)": 400,
      "Tỷ lệ trúng tuyển (%)": "?(2)",
      "Điểm chuẩn (Điểm)": 22
    },
    {
      "name": "Tổng cộng",
      "SL dự thi (Ngàn người)": 1,
      "Tỷ lệ trúng tuyển (%)": 26,
      "Điểm chuẩn (Điểm)": "-"
    }
  ],
  "options": {
    "bars": [
      "SL dự thi (Ngàn người)",
      "Tỷ lệ trúng tuyển (%)",
      "Điểm chuẩn (Điểm)"
    ]
  }
}
\`\`\``,
    stem: `Số lượng thí sinh đăng ký dự thi Khối A01 (giá trị ?(1)) là bao nhiêu ngàn người?`,
    choices: [
      { id: 'A', text: '250 ngàn người.' },
      { id: 'B', text: '300 ngàn người.' },
      { id: 'C', text: '350 ngàn người.' },
      { id: 'D', text: '400 ngàn người.' }
    ],
    answer: 'B',
    solution: `- Tổng số thí sinh dự thi cả trường = 1.000 ngàn người.
- Thí sinh Khối A00 = 300; Khối D01 = 400.
- Thí sinh Khối A01 = $1000 - 300 - 400 = 300$ (ngàn người).

**Chọn B.***`,
    hint1: `Lấy Tổng số dự thi trừ đi Khối A00 và Khối D01.`,
    hint2: `1000 - 300 - 400 = 300.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_98',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Tuyển sinh Đại học X (2024)',
    stem: `Số lượng thí sinh trúng tuyển vào Khối D01 là bao nhiêu ngàn người?`,
    choices: [
      { id: 'A', text: '85 ngàn người.' },
      { id: 'B', text: '90 ngàn người.' },
      { id: 'C', text: '95 ngàn người.' },
      { id: 'D', text: '100 ngàn người.' }
    ],
    answer: 'C',
    solution: `- Tổng số thí sinh trúng tuyển cả trường = $1000 \times 26\% = 260$ (ngàn người).
- Số thí sinh trúng tuyển Khối A00 = $300 \times 25\% = 75$ (ngàn).
- Số thí sinh trúng tuyển Khối A01 = $300 \times 30\% = 90$ (ngàn).
- Số thí sinh trúng tuyển Khối D01 = $260 - 75 - 90 = 95$ (ngàn người).

**Chọn C.***`,
    hint1: `Tính tổng số trúng tuyển toàn trường. Sau đó tính số trúng tuyển của A00 và A01 rồi trừ ra để lấy phần của D01.`,
    hint2: `Tổng trúng tuyển = 260. A00 trúng 75. A01 trúng 90. D01 trúng 95.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'data-table']
  },
  {
    id: 'dgnl_data_99',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Tuyển sinh Đại học X (2024)',
    stem: `Tỷ lệ trúng tuyển của Khối D01 (giá trị ?(2)) là bao nhiêu phần trăm?`,
    choices: [
      { id: 'A', text: '22,50%.' },
      { id: 'B', text: '23,75%.' },
      { id: 'C', text: '24,00%.' },
      { id: 'D', text: '25,00%.' }
    ],
    answer: 'B',
    solution: `- Số lượng thí sinh trúng tuyển Khối D01 = 95 ngàn (đã tính ở câu trước).
- Số lượng dự thi Khối D01 = 400 ngàn.
- Tỷ lệ trúng tuyển Khối D01 = $\frac{95}{400} \times 100\% = 23,75\%$.

**Chọn B.***`,
    hint1: `Lấy số người trúng tuyển Khối D01 (95 ngàn) chia cho tổng số dự thi Khối D01 (400 ngàn).`,
    hint2: `95 / 400 = 23.75%.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'proportion']
  },
  {
    id: 'dgnl_data_100',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Tuyển sinh Đại học X (2024)',
    stem: `Giả sử điểm trung bình của các thí sinh trúng tuyển mỗi khối đúng bằng mức điểm chuẩn của khối đó. Khi đó, điểm chuẩn trung bình của toàn bộ các thí sinh trúng tuyển vào trường Đại học X năm 2024 (của cả 3 khối) xấp xỉ bằng bao nhiêu?`,
    choices: [
      { id: 'A', text: '22,85 điểm.' },
      { id: 'B', text: '23,07 điểm.' },
      { id: 'C', text: '23,17 điểm.' },
      { id: 'D', text: '23,50 điểm.' }
    ],
    answer: 'B',
    solution: `Đây là bài toán Trung bình có trọng số. Trọng số ở đây chính là **Số lượng thí sinh trúng tuyển** của mỗi khối.
- Khối A00: 75 ngàn người, điểm 24,5.
- Khối A01: 90 ngàn người, điểm 23,0.
- Khối D01: 95 ngàn người, điểm 22,0.
- Tổng trúng tuyển: 260 ngàn người.
Điểm trung bình = $\frac{75 \times 24,5 + 90 \times 23,0 + 95 \times 22,0}{260}$
$= \frac{1837,5 + 2070 + 2090}{260} = \frac{5997,5}{260} \approx 23,067$ (điểm).

**Chọn B.***`,
    hint1: `Sử dụng công thức Trung bình có trọng số. Trọng số là "Số người trúng tuyển" chứ không phải "Số người dự thi"!`,
    hint2: `(75*24.5 + 90*23 + 95*22) / 260.`,
    estimatedSeconds: 45,
    tags: ['data_analysis', 'dgnl', 'weighted-average']
  },
  {
    id: 'dgnl_data_101',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu kim ngạch Xuất khẩu Quốc gia Y',
    passageText: `Hai biểu đồ tròn so sánh Cơ cấu Kim ngạch Xuất khẩu của Quốc gia Y trong hai năm 2010 và 2023.
- Tổng kim ngạch 2010: 100 tỷ USD. Gồm Nông sản (50%), Công nghiệp (30%), Dịch vụ (20%).
- Tổng kim ngạch 2023: 400 tỷ USD. Gồm Nông sản (20%), Công nghiệp (60%), Dịch vụ (20%).`,
    stem: `Giá trị kim ngạch xuất khẩu của nhóm ngành Công nghiệp năm 2023 cao gấp bao nhiêu lần so với năm 2010?`,
    choices: [
      { id: 'A', text: '2 lần.' },
      { id: 'B', text: '4 lần.' },
      { id: 'C', text: '6 lần.' },
      { id: 'D', text: '8 lần.' }
    ],
    answer: 'D',
    solution: `- Năm 2010: Kim ngạch Công nghiệp = $30\% \times 100 = 30$ (tỷ USD).
- Năm 2023: Kim ngạch Công nghiệp = $60\% \times 400 = 240$ (tỷ USD).
- Gấp số lần = $\frac{240}{30} = 8$ (lần).

**Chọn D.***`,
    hint1: `Tính giá trị tuyệt đối của ngành Công nghiệp ở 2 năm rồi lập tỉ số. Không lấy phần trăm chia nhau.`,
    hint2: `Năm 2023 là 240. Năm 2010 là 30. Gấp 8 lần.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'pie-chart']
  },
  {
    id: 'dgnl_data_102',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 2,
    cognitiveLevel: 'M2',
    type: 'single-choice',
    passageTitle: 'Cơ cấu kim ngạch Xuất khẩu Quốc gia Y',
    stem: `Nhóm ngành nào có giá trị xuất khẩu (tính bằng số tuyệt đối) tăng trưởng ít nhất trong khoảng thời gian từ 2010 đến 2023?`,
    choices: [
      { id: 'A', text: 'Nông sản.' },
      { id: 'B', text: 'Công nghiệp.' },
      { id: 'C', text: 'Dịch vụ.' },
      { id: 'D', text: 'Nông sản và Dịch vụ bằng nhau.' }
    ],
    answer: 'A',
    solution: `Ta tính độ tăng tuyệt đối của từng ngành: - Nông sản: 2010 ($50\% \times 100 = 50$); 2023 ($20\% \times 400 = 80$). Tăng $+30$ (tỷ USD).
- Công nghiệp: Tăng $+210$ (tỷ USD) (từ 30 lên 240).
- Dịch vụ: 2010 ($20\% \times 100 = 20$); 2023 ($20\% \times 400 = 80$). Tăng $+60$ (tỷ USD).
Vậy nhóm Nông sản tăng trưởng tuyệt đối ít nhất ($+30$).

**Chọn A.***`,
    hint1: `Tính số tuyệt đối của Dịch vụ và Nông sản ở 2 năm để biết nhóm nào tăng ít nhất.`,
    hint2: `Nông sản tăng 30. Dịch vụ tăng 60.`,
    estimatedSeconds: 30,
    tags: ['data_analysis', 'dgnl', 'comparison']
  },
  {
    id: 'dgnl_data_103',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de'],
    difficulty: 1,
    cognitiveLevel: 'M1',
    type: 'single-choice',
    passageTitle: 'Cơ cấu kim ngạch Xuất khẩu Quốc gia Y',
    stem: `Trung bình mỗi năm trong giai đoạn từ 2010 đến 2023 (đúng 13 năm), Tổng kim ngạch xuất khẩu của Quốc gia Y đã tăng thêm bao nhiêu tỷ USD/năm? (Làm tròn đến 2 chữ số thập phân).`,
    choices: [
      { id: 'A', text: '18,50 tỷ/năm.' },
      { id: 'B', text: '20,00 tỷ/năm.' },
      { id: 'C', text: '23,08 tỷ/năm.' },
      { id: 'D', text: '30,76 tỷ/năm.' }
    ],
    answer: 'C',
    solution: `- Tổng kim ngạch tăng thêm trong cả giai đoạn = $400 - 100 = 300$ (tỷ USD).
- Tổng thời gian = 13 năm.
- Trung bình mỗi năm tăng = $\frac{300}{13} \approx 23,0769 \approx 23,08$ (tỷ USD/năm).

**Chọn C.***`,
    hint1: `Tính tổng mức tăng thêm của cả quốc gia từ 2010 đến 2023, rồi chia đều cho 13 năm.`,
    hint2: `(400 - 100) / 13 = 23.08.`,
    estimatedSeconds: 15,
    tags: ['data_analysis', 'dgnl', 'average']
  },
  {
    id: 'dgnl_data_104',
    exam: 'HSA',
    domain: 'toan-hoc',
    topic: 'phan-tich-so-lieu',
    competency: ['giai-quyet-van-de', 'mo-hinh-hoa'],
    difficulty: 3,
    cognitiveLevel: 'M3',
    type: 'single-choice',
    passageTitle: 'Cơ cấu kim ngạch Xuất khẩu Quốc gia Y',
    stem: `Giả sử trong giai đoạn từ năm 2023 đến năm 2030, giá trị kim ngạch xuất khẩu của nhóm ngành Nông sản không tăng thêm mà giữ nguyên bằng mức của năm 2023. Nếu Quốc gia Y đặt mục tiêu giảm tỷ trọng của Nông sản trong cơ cấu xuất khẩu năm 2030 xuống chỉ còn 10%, thì Tổng kim ngạch xuất khẩu của toàn quốc gia vào năm 2030 phải đạt bao nhiêu tỷ USD?`,
    choices: [
      { id: 'A', text: '600 tỷ USD.' },
      { id: 'B', text: '800 tỷ USD.' },
      { id: 'C', text: '1000 tỷ USD.' },
      { id: 'D', text: '1200 tỷ USD.' }
    ],
    answer: 'B',
    solution: `- Giá trị kim ngạch Nông sản năm 2023 = 80 tỷ USD.
- Năm 2030, kim ngạch Nông sản vẫn giữ nguyên là 80 tỷ USD.
- Mặt khác, tỷ trọng Nông sản năm 2030 chiếm 10% Tổng kim ngạch năm 2030.
$\rightarrow$ Tổng kim ngạch năm 2030 = $\frac{80}{10\%} = 800$ (tỷ USD).

**Chọn B.***`,
    hint1: `Tìm giá trị xuất khẩu Nông sản năm 2023 (là 80). Đây cũng sẽ là giá trị của năm 2030. Lấy 80 chia cho mục tiêu 10%.`,
    hint2: `80 / 10% = 800.`,
    estimatedSeconds: 25,
    tags: ['data_analysis', 'dgnl', 'optimization']
  }
]
